import { createSelector } from "@reduxjs/toolkit";
import {
  CurrencyFormatters,
  emailRegex,
  getAgentErrorSubtitle,
  getAgentErrorTitle,
  getCheckoutCreditBreakdown,
  getCreditBreakdownAvailableByProduct,
  getCreditBreakdownIneligibleByProduct,
  getCreditsAvailableByProduct,
  getHSPEarnOfferDescription,
  getIsFreeBreakfast,
  getRewardsString,
  getSubtotalWithAncillariesLegacy,
  getTotalPriceText,
  IconName,
  IHotelPriceLineItem,
  paymentTextConstants,
  phoneRegex,
  roundToTwoDecimals,
  TEST_CARD_LAST_FOURS,
  truncateToTwoDecimals,
  twoDecimalFormatter,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
  DEFAULT_CHECK_IN_MINIMUM_AGE,
} from "halifax";
import {
  AncillaryKindEnum,
  AvailableProductEnum,
  CallState,
  CancellationPolicyEnum,
  Cap1HotelCfarRRProperties,
  CfarOfferType,
  CompleteBuyHotelProperties,
  CorpLodging,
  CorpRoomProduct,
  CustomerAccountRole,
  ErrorModalType,
  ErrorTitles,
  FiatPrice,
  getCompleteBuyHotelHotelCfarOfferFacts,
  getNthNightTrackingProperty,
  getPromotionTrackingProperties,
  getRewardsAccountMinimumRequirementState,
  getSubtitleTrackingValue,
  HotelBookErrorType,
  HotelBookType,
  HotelCfarQuote,
  HotelPriceQuoteScheduleRequestV2,
  HotelPricing,
  HotelQuoteScheduleRequest,
  ITrackingProperties,
  Lodging,
  LodgingCollectionEnum,
  MerchantOfRecord,
  MODAL_ALERT,
  ModalAlertProperties,
  ModalCategoryType,
  ModalScreens,
  NO_AVAILABILITY_HOTELS,
  OffersForProductsRequest,
  PartialCompleteBuyHotelHotelCfarOfferFacts,
  PassengerAncillaryPricing,
  PaymentAmountEnum,
  PaymentErrorEnum,
  PaymentSplitRequest,
  PaymentSplitRequestEnum,
  PaymentType,
  PaymentV2Enum,
  PaymentVerifyResultEnum,
  Place,
  PriceQuoteHotelProperties,
  Prices,
  ProductClassEnum,
  RestrictedToProductsEnum,
  ReviewDetailsHotelCheckoutProperties,
  RewardsAccount,
  RewardsAccountMinimumRequirementState,
  RewardsPaymentType,
  RewardsPrice,
  SplitPaymentType,
  TypeOfPaymentEnum,
  UserCardPaymentType,
  ViewedHotelDetailsProperties,
  ICorpPerson,
} from "redmond";

import type { CfarRateQuoteSuccess } from "@b2bportal/lodging-api";
import { LodgingErrorEnum } from "@b2bportal/lodging-api";
import {
  ErrorCode,
  Payment,
  PaymentError,
  PaymentOpaqueValue,
  ProductError,
  PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { isCorpTenant } from "@capone/common";
import dayjs from "dayjs";
import queryStringParser from "query-string";
import {
  HtsAncillaryId,
  HtsAncillaryIdType,
  Person,
} from "redmond/apis/tysons/payment-machine";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { IStoreState } from "../../../../reducers/types";
import {
  DO_NOT_APPLY_OPTION_KEY,
  getHasAddedCfarFromModalSelector,
  isLodgingErrorCopyUpdateEnabledSelector,
  selectedCfarIdSelector,
} from "../../../ancillary/reducer";
import {
  getHotelAvailabilityFromDate,
  getHotelAvailabilityUntilDate,
  getSearchedNightCount as getSearchedNightCountInAvailability,
  getSelectedLodgingIndex as getSelectedLodgingIndexInAvailability,
} from "../../../availability/reducer";
import {
  getAgentEmail,
  getAllowRewardsWithPolicy,
  getRewardsAccounts,
  getRewardsAccountWithLargestValue,
  getSelectedAccount,
  getSelectedAccountReferenceIdIfRedemptionEnabled,
} from "../../../rewards/reducer";
import { resolvePayFlexCardProduct } from "./payFlexCardProduct";
import {
  getFromDate as getFromDateInSearch,
  getNightCount as getNightCountInSearch,
  getRoomsCount as getRoomsCountInSearch,
  getUntilDate as getUntilDateInSearch,
} from "../../../search/reducer";
import {
  additionalInfoFromChosenRoomProductSelector,
  getAddedAncillariesPricingV2 as getAddedAncillariesPricingInShopV2,
  // TODO - PF exercise: some of its properties will be missing in the PF exercise flow (because /hotel/shop is not called)
  getHotelCfarOfferChoicePropertiesSelector,
  getHotelShopChosenProduct,
  getHotelShopChosenProductHotelLoyaltyProgramCode,
  getHotelShopChosenProductIndex,
  getHotelShopChosenRoomInfo,
  getHotelShopEntryPoint,
  getReceivedCrossSellTrackingProperties,
  getHotelShopPersistentLodgingTrackingProperties,
  getHotelShopRecommendedBasedOnPreferences,
  getHotelShopSelectedLodging,
  getViewedHotelDetailsProperties,
  hotelCfarQuoteFromChosenRoomProductSelector as hotelCfarQuoteFromChosenRoomProductInShopSelector,
  hotelCfarQuoteFromChosenRoomProductSelector,
  hotelCfarQuoteFromChosenRoomProductV2Selector,
} from "../../../shop/reducer/selectors";
import { getCreditBreakdown } from "../../../travel-wallet/reducer";
import { getLoyaltyNumberForChosenProduct } from "../../components/capone-corporate/LoyaltyInformationForm/component";
import { toErrorString } from "../../sagas/hotel-book/pollConfirmationDetailsSaga";
import { applyDiscountValueToFiatPrice, getAbsolutePrices } from "../../utils";
import * as textConstants from "./textConstants";

export const getUserPassengers = (state: IStoreState) =>
  state.hotelBook.userPassengers;

export const getUserSelectedTravelerId = (state: IStoreState) =>
  state.hotelBook.userSelectedTravelerId;

export const getUserSelectedTravelerIds = (state: IStoreState) =>
  state.hotelBook.userSelectedTravelerIds;

export const getUserSelectedTravelersList = (state: IStoreState) =>
  state.hotelBook.userSelectedTravelersList;

export const getUserSelectedTravelersFromIds = createSelector(
  getUserPassengers,
  getUserSelectedTravelerIds,
  (passengers, selectedIds) =>
    passengers.filter((passenger) => selectedIds.includes(passenger.id))
);

export const getUserPassengerCallState = (state: IStoreState) =>
  state.hotelBook.userPassengerCallState;

export const getConfirmationEmail = (state: IStoreState) =>
  state.hotelBook.confirmationEmailAddress;

export const getConfirmationPhoneNumber = (state: IStoreState) =>
  state.hotelBook.confirmationPhoneNumber;

export const getPaymentMethods = (state: IStoreState) =>
  state.hotelBook.paymentMethods;

export const getPaymentMethod = (state: IStoreState) =>
  state.hotelBook.paymentMethod;

export const getSelectedPaymentMethodId = (state: IStoreState) =>
  state.hotelBook.selectedPaymentMethodId;

export const getListPaymentMethodsCallState = (state: IStoreState) =>
  state.hotelBook.listPaymentMethodCallState;

export const getDeletePaymentMethodCallState = (state: IStoreState) =>
  state.hotelBook.deletePaymentMethodCallState;

export const getFetchPaymentMethodCallState = (state: IStoreState) =>
  state.hotelBook.fetchPaymentMethodCallState;

export const getVerifyPaymentMethodCallState = (state: IStoreState) =>
  state.hotelBook.verifyPaymentMethodCallState;

export const getSession = (state: IStoreState) => state.hotelBook.session;

export const getPriceQuote = (state: IStoreState) => state.hotelBook.priceQuote;

export const getPricingWithAncillaries = (state: IStoreState) =>
  state.hotelBook.pricingWithAncillaries;

export const getHotelAncillaryQuotes = (state: IStoreState) =>
  state.hotelBook.hotelAncillaryQuotes;

export const hotelBookTypeSelector = (state: IStoreState) =>
  state.hotelBook.hotelBookType;

export const isReadyToDisplayHotelSummaryDetailsSelector = () => true;

export const getHotelBookChosenProduct = createSelector(
  getHotelShopChosenProduct,
  (hotelShopChosenProduct) => hotelShopChosenProduct
);

export const getHotelBookChosenProductIndex = createSelector(
  getHotelShopChosenProductIndex,
  (hotelShopChosenProductIndex) => hotelShopChosenProductIndex
);

export const getHotelBookChosenRoomInfo = createSelector(
  getHotelShopChosenRoomInfo,
  (hotelShopChosenRoomInfo) => hotelShopChosenRoomInfo
);

export const getHotelBookSelectedAvailability = createSelector(
  getHotelShopSelectedLodging,
  (hotelShopSelectedAvailability): Lodging | CorpLodging | null =>
    hotelShopSelectedAvailability
);

export const getHotelBookSelectedAvailabilityCheckInMinimumAge = createSelector(
  getHotelBookSelectedAvailability,
  (hotelBookSelectedAvailability): number | undefined =>
    // minimumAge can be returned as "0", so we should check for > 0 to avoid showing it in the UI copy.
    hotelBookSelectedAvailability?.lodging?.checkInInstructions?.minimumAge ||
    DEFAULT_CHECK_IN_MINIMUM_AGE
);

export const getFromDateInBook = createSelector(
  getFromDateInSearch,
  getHotelAvailabilityFromDate,
  (fromDateInSearch, hotelAvailabilityFromDate): Date | null =>
    hotelAvailabilityFromDate ?? fromDateInSearch
);

export const getNightCountInBook = createSelector(
  getNightCountInSearch,
  (nightCountInSearch) => nightCountInSearch
);

export const getUntilDateInBook = createSelector(
  getUntilDateInSearch,
  getHotelAvailabilityUntilDate,
  (untilDateInSearch, hotelAvailabilityUntilDate): Date | null =>
    hotelAvailabilityUntilDate ?? untilDateInSearch
);

export const getRoomsCountInBook = createSelector(
  getRoomsCountInSearch,
  (roomsCountInSearch) => roomsCountInSearch
);

export const getSearchedNightCountInBook = createSelector(
  getSearchedNightCountInAvailability,
  getFromDateInSearch,
  getUntilDateInBook,
  (searchedNightCountInAvailability, fromDate, untilDate) =>
    searchedNightCountInAvailability ?? dayjs(untilDate).diff(fromDate, "days")
);

export const getSelectedLodgingIndexInBook = createSelector(
  getSelectedLodgingIndexInAvailability,
  (selectedLodgingIndexInAvailability) => selectedLodgingIndexInAvailability
);

export const getPurchasePriceQuotePricing = createSelector(
  getPriceQuote,
  getPricingWithAncillaries,
  (
    priceQuote,
    pricingWithAncillaries
  ):
    | {
        payNowTotal: Prices;
        hotelPricing?: HotelPricing;
      }
    | undefined => {
    /*
      For Hotel Ancillaries, the priceQuote is only the hotelQuote and doesn't include
      the ancillary costs. The ancillary costs are included in the combinedPricing that
      comes from the BE and is stored in the priceQuoteWithAncillaries. If
      priceQuoteWithAncillaries is available, we want to use that for the total amount due.
    */
    const hotelPricing = pricingWithAncillaries || priceQuote?.pricing;

    return hotelPricing
      ? {
          payNowTotal: hotelPricing.payNowTotal,
          hotelPricing,
        }
      : undefined;
  }
);

// @FLEX-TODO: Once rolled out, we need to remove the old selector
export const getHotelCfarQuoteInCheckoutV2 = createSelector(
  getHotelAncillaryQuotes,
  hotelCfarQuoteFromChosenRoomProductV2Selector,
  (
    hotelAncillaryQuotes,
    hotelCfarQuoteFromChosenRoomProductInShopV2
  ): HotelCfarQuote | CfarRateQuoteSuccess | undefined =>
    hotelAncillaryQuotes?.cfar ?? hotelCfarQuoteFromChosenRoomProductInShopV2
);

export const getHotelCfarQuoteInCheckout = createSelector(
  getHotelAncillaryQuotes,
  hotelCfarQuoteFromChosenRoomProductInShopSelector,
  (
    hotelAncillaryQuotes,
    hotelCfarQuoteFromChosenRoomProductInShop
  ): HotelCfarQuote | null =>
    hotelAncillaryQuotes?.cfar ?? hotelCfarQuoteFromChosenRoomProductInShop
);

export const isNegativePremiumCfarSelector = createSelector(
  [hotelCfarQuoteFromChosenRoomProductV2Selector, getHotelAncillaryQuotes],
  (hotelCfarQuoteFromShop, hotelAncillaryQuotes) => {
    if (hotelAncillaryQuotes?.cfar) {
      return hotelAncillaryQuotes.cfar.premiumPrices.fiat.value < 0;
    }

    return (
      hotelCfarQuoteFromShop != null &&
      hotelCfarQuoteFromShop.premiumAmount.fiat.value < 0
    );
  }
);

// This is the "discount" that the negative premium CFAR product provides.
export const cfarNegativePremiumDiscountSelector = createSelector(
  [
    isNegativePremiumCfarSelector,
    hotelCfarQuoteFromChosenRoomProductV2Selector,
    getHotelAncillaryQuotes,
  ],
  (
    isNegativePremiumCfar,
    hotelCfarQuoteFromChosenRoomProduct,
    hotelAncillaryQuotes
  ) => {
    if (!isNegativePremiumCfar || hotelCfarQuoteFromChosenRoomProduct == null) {
      return null;
    }

    if (hotelAncillaryQuotes?.cfar != null) {
      return getAbsolutePrices(hotelAncillaryQuotes.cfar.premiumPrices);
    }

    return getAbsolutePrices(hotelCfarQuoteFromChosenRoomProduct.premiumAmount);
  }
);

export const getPriceQuoteErrors = (state: IStoreState) =>
  state.hotelBook.priceQuoteErrors;

export const getSchedulePriceQuoteError = (state: IStoreState) =>
  state.hotelBook.schedulePriceQuoteError;

export const getScheduleBookError = (state: IStoreState) =>
  state.hotelBook.scheduleBookError;

export const getPollPriceQuoteCallState = (state: IStoreState) =>
  state.hotelBook.pollPriceQuoteCallState;

export const getSchedulePriceQuoteCallState = (state: IStoreState) =>
  state.hotelBook.schedulePriceQuoteCallState;

export const getConfirmationDetails = (state: IStoreState) =>
  state.hotelBook.confirmationDetails;

export const getUserInfo = (state: IStoreState) => state.hotelRewards.userInfo;

export const getConfirmationDetailsError = (state: IStoreState) =>
  state.hotelBook.confirmationDetailsErrors;

export const getConfirmationDetailsCallState = (state: IStoreState) =>
  state.hotelBook.confirmationDetailsCallState;

export const getOffers = (state: IStoreState) =>
  filterOutSpendAndGetOffers(state.hotelBook.offers ?? []);

export const getOfferToApply = (state: IStoreState) => {
  const offer = state.hotelBook.offerToApply;
  // Don't return spend-and-get offers
  if (offer && isSpendAndGetOffer(offer.descriptions)) return undefined;
  return offer;
};

export const getOfferAmountToApplySelector = createSelector(
  getOfferToApply,
  (offer) => offer?.amount
);

export const getCredit = createSelector(
  [(state: IStoreState) => state.hotelBook.credit],
  (credit) => {
    if (credit) {
      return {
        ...credit,
        amount: {
          ...credit.amount,
          amount: truncateToTwoDecimals(credit.amount.amount),
        },
      } satisfies typeof credit;
    }
    return credit;
  }
);

export const getApplicableCredit = createSelector(getCredit, (credit) =>
  credit
    ? getCreditsAvailableByProduct(RestrictedToProductsEnum.Lodging, credit)
    : credit
);

export const getApplicableCreditsBreakdown = createSelector(
  getCreditBreakdown,
  (creditBreakdown) =>
    getCreditBreakdownAvailableByProduct(
      RestrictedToProductsEnum.Lodging,
      creditBreakdown
    )
);

export const getIneligibleCreditsBreakdown = createSelector(
  getCreditBreakdown,
  (creditBreakdown) =>
    getCreditBreakdownIneligibleByProduct(
      RestrictedToProductsEnum.Lodging,
      creditBreakdown
    )
);

export const getCreditToApply = createSelector(
  [(state: IStoreState) => state.hotelBook.creditToApply],
  (credit) => {
    if (credit) {
      return {
        ...credit,
        amount: {
          ...credit.amount,
          amount: truncateToTwoDecimals(credit.amount.amount),
        },
      } satisfies typeof credit;
    }
    return credit;
  }
);

export const getTravelWalletItemsToApply = createSelector(
  getOfferToApply,
  getCreditToApply,
  (offerToApply, creditToApply) => ({
    offerToApply,
    creditToApply,
  })
);

export const isTravelOffersOrCreditAvailableSelector = createSelector(
  getOffers,
  getApplicableCredit,
  (offers, credit): boolean => (!!offers && offers.length > 0) || !!credit
);

export const getFetchApplicableTravelWalletItemsCallState = (
  state: IStoreState
) => state.hotelBook.fetchApplicableTravelWalletItemsCallState;

export const getBestOfferOverall = (state: IStoreState) =>
  state.hotelBook.bestOfferOverall;

export const getRoomProductPricingEstimate = createSelector(
  getHotelBookChosenProduct,
  (product) => product?.total || null
);

export const getRoomProductPricingEstimateWithAllFees = createSelector(
  getHotelBookChosenProduct,
  (product) => product?.tripTotal || null
);

export const getAddedAncillariesPricingInCheckout = createSelector(
  getAddedAncillariesPricingInShopV2,
  getHotelCfarQuoteInCheckoutV2,
  (addedAncillaryPricingV2, hotelCfarQuoteV2): PassengerAncillaryPricing[] =>
    addedAncillaryPricingV2.map((pricing) => {
      switch (pricing.kind) {
        case AncillaryKindEnum.Cfar: {
          if (hotelCfarQuoteV2) {
            const premium = (
              "premiumAmount" in hotelCfarQuoteV2
                ? hotelCfarQuoteV2.premiumAmount
                : hotelCfarQuoteV2.premiumPrices
            ) as Prices;
            const coverageAmount = {
              amount:
                "fiat" in hotelCfarQuoteV2.coverageAmount
                  ? hotelCfarQuoteV2.coverageAmount.fiat.value
                  : hotelCfarQuoteV2.coverageAmount.amount,
              currency:
                "fiat" in hotelCfarQuoteV2.coverageAmount
                  ? hotelCfarQuoteV2.coverageAmount.fiat.currencyCode
                  : hotelCfarQuoteV2.coverageAmount.currency,
            };

            const coverageOverriding =
              "coverageOverriding" in hotelCfarQuoteV2
                ? hotelCfarQuoteV2.coverageOverriding
                : undefined;

            return {
              ...pricing,
              premium,
              coverageAmount,
              coverageOverriding,
            };
          }
          return pricing;
        }
        default: {
          return pricing;
        }
      }
    })
);

export const getPricingEstimateTotal = createSelector(
  getRoomProductPricingEstimate,
  getAddedAncillariesPricingInShopV2,
  (roomProductEstimate, addedAncillaryPricingV2): Prices | null =>
    roomProductEstimate
      ? getSubtotalWithAncillariesLegacy(
          roomProductEstimate,
          addedAncillaryPricingV2
        )
      : null
);

export const getPricingEstimateTotaWithAllFees = createSelector(
  getRoomProductPricingEstimateWithAllFees,
  getAddedAncillariesPricingInShopV2,
  (roomProductEstimate, addedAncillaryPricingV2): Prices | null =>
    roomProductEstimate
      ? getSubtotalWithAncillariesLegacy(
          roomProductEstimate,
          addedAncillaryPricingV2
        )
      : null
);

export const getPriceDifferenceAcknowledged = (state: IStoreState) =>
  state.hotelBook.priceDifferenceAcknowledged;

export const getHasAttemptedReshop = (state: IStoreState) =>
  state.hotelBook.hasAttemptedReshop;

export const getShowReshopSuccessBanner = (state: IStoreState) =>
  state.hotelBook.showReshopSuccessBanner;

export const getIsReshopQuotePending = (state: IStoreState) =>
  state.hotelBook.isReshopQuotePending;

export const getShowConnectionRetryBanner = (state: IStoreState) =>
  state.hotelBook.showConnectionRetryBanner;

export const getIsConnectionRetryPending = (state: IStoreState) =>
  state.hotelBook.isConnectionRetryPending;

// Either requote-success banner (reshop or connection-retry) is showing; drives
// the mobile step-advance + desktop re-lock recovery so both paths resume alike.
export const getShowRequoteSuccessBanner = createSelector(
  getShowReshopSuccessBanner,
  getShowConnectionRetryBanner,
  (reshop, connectionRetry): boolean => reshop || connectionRetry
);

// Either requote (reshop or connection-retry) is mid-flight, before its success
// banner is set. Paired with getShowRequoteSuccessBanner to guard the offer/
// wallet card reset across both windows so a requote never wipes the chosen
// card (CT1261T-419).
export const getIsRequotePending = createSelector(
  getIsReshopQuotePending,
  getIsConnectionRetryPending,
  (reshop, connectionRetry): boolean => reshop || connectionRetry
);

export const getPriceDifference = createSelector(
  getPricingEstimateTotal,
  getPriceDifferenceAcknowledged,
  getPurchasePriceQuotePricing,
  (
    estimate,
    acknowledged,
    purchasePriceQuotePricing
  ): {
    hasDifference: boolean;
    isIncrease: boolean;
    predictedTotal: number;
    priceQuoteTotal: number;
    amount?: string;
    fiatAmount?: FiatPrice;
  } => {
    if (!purchasePriceQuotePricing || !estimate || acknowledged) {
      return {
        hasDifference: false,
        isIncrease: false,
        predictedTotal: 0,
        priceQuoteTotal: 0,
      };
    }

    const predictedTotal = estimate.fiat.value;
    const priceQuoteTotal = purchasePriceQuotePricing.payNowTotal.fiat.value;
    const delta = Math.abs(priceQuoteTotal - predictedTotal);

    return {
      // If the difference is within a dollar we don't show the error modal
      hasDifference: delta >= 1,
      isIncrease: priceQuoteTotal > predictedTotal,
      amount: delta.toFixed(0).toString(),
      // Delta as a FiatPrice so copy formats it in the quote's currency, not a
      // hardcoded symbol.
      fiatAmount: {
        value: delta,
        currencySymbol:
          purchasePriceQuotePricing.payNowTotal.fiat.currencySymbol,
        currencyCode: purchasePriceQuotePricing.payNowTotal.fiat.currencyCode,
      },
      predictedTotal,
      priceQuoteTotal,
    };
  }
);

// CT1261T-421: a price difference keeps the legacy blocking modal unless it's
// a flag-on consumer decrease, which is shown inline instead (corp has no
// inline-message UI yet). Shared by every price-difference gate so they can't
// drift apart.
const isPriceDifferenceBlocking = (
  priceDifference: ReturnType<typeof getPriceDifference>,
  isLodgingErrorCopyUpdateEnabled: boolean
): boolean =>
  priceDifference.isIncrease ||
  !isLodgingErrorCopyUpdateEnabled ||
  isCorpTenant(config.TENANT);

export const getRewardsConversionFailed = (state: IStoreState) =>
  state.hotelBook.rewardsConversionFailed;

export const getHasError = createSelector(
  getUserPassengerCallState,
  getPriceDifference,
  getPriceDifferenceAcknowledged,
  getPollPriceQuoteCallState,
  getScheduleBookError,
  getSchedulePriceQuoteError,
  getConfirmationDetailsCallState,
  getRewardsConversionFailed,
  getVerifyPaymentMethodCallState,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    passengerCallState,
    priceDifference,
    priceDifferenceAcknowledged,
    priceQuoteCallState,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsCallState,
    rewardsConversionFailed,
    verifyPaymentMethodCallState,
    isLodgingErrorCopyUpdateEnabled
  ): boolean =>
    passengerCallState === CallState.Failed ||
    // CT1261T-421: a non-blocking decrease isn't an error.
    (priceDifference.hasDifference &&
      !priceDifferenceAcknowledged &&
      isPriceDifferenceBlocking(
        priceDifference,
        isLodgingErrorCopyUpdateEnabled
      )) ||
    !!rewardsConversionFailed ||
    !!scheduleBookError ||
    !!schedulePriceQuoteError ||
    verifyPaymentMethodCallState === CallState.Failed ||
    confirmationDetailsCallState === CallState.Failed ||
    priceQuoteCallState === CallState.Failed
);

// The code sits directly on `ErrorCode`, or nested in a `ProductError`'s opaque
// value as `LodgingError`.
const getHotelBookErrorCode = (error: PurchaseError): string | undefined => {
  switch (error.Error) {
    case PurchaseErrorEnum.ErrorCode:
      return (error as ErrorCode).code;
    case PurchaseErrorEnum.ProductError: {
      const productError = error as ProductError;
      return (productError.value.value?.LodgingError ||
        productError.value.value) as string;
    }
    default:
      return undefined;
  }
};

// Error copy + a connection flag that getIsConnectionError reads back, so the
// classification isn't re-derived (CT1261T-419).
type ResolvedErrorTitles = ErrorTitles & { isConnection?: boolean };

const getErrorMessage = ({
  error,
  checkInMinimumAge = undefined,
  isLodgingErrorCopyUpdateEnabled = false,
}: {
  error: PurchaseError;
  checkInMinimumAge?: number;
  isLodgingErrorCopyUpdateEnabled?: boolean;
}): ResolvedErrorTitles => {
  const code = getHotelBookErrorCode(error) ?? "";
  let agentSubtitle = getAgentErrorSubtitle(error.Error);
  let agentTitle = getAgentErrorTitle(error.Error);
  // Unmapped/unknown codes resolve here: FALLBACK (CT1261T-422) when the flag is
  // on, legacy GENERIC when off. Matched cases below overwrite this.
  let titles = isLodgingErrorCopyUpdateEnabled
    ? textConstants.FALLBACK_ERROR_TITLES
    : textConstants.GENERIC_ERROR_TITLES();
  // Flags connection/quote-expired codes; read back via getIsConnectionError.
  let isConnection = false;
  const handleNoAvailability = (
    code:
      | HotelBookErrorType.LackOfInventory
      | HotelBookErrorType.RateNotAvailable
      | HotelBookErrorType.RateSoldOut
      | HotelBookErrorType.RoomSoldOut
      | HotelBookErrorType.RateChanged
  ) => {
    if (isLodgingErrorCopyUpdateEnabled) {
      titles = textConstants.UNAVAILABLE_OPTION_TITLES;
    } else {
      switch (code) {
        case HotelBookErrorType.LackOfInventory:
        case HotelBookErrorType.RateSoldOut:
        case HotelBookErrorType.RoomSoldOut:
          titles = textConstants.LACK_OF_INVENTORY_TITLES;
          break;
        case HotelBookErrorType.RateNotAvailable:
        case HotelBookErrorType.RateChanged:
          titles = textConstants.RATE_NOT_AVAILABLE_TITLES;
          break;
      }
    }

    trackEvent({
      eventName: NO_AVAILABILITY_HOTELS,
      properties: {},
    });
  };

  const getPaymentErrorTitles = () => textConstants.PAYMENT_ERROR_TITLES;

  switch (error.Error) {
    case PurchaseErrorEnum.PaymentError:
      const paymentError = error as PaymentError;
      if (paymentError.value.type === Payment.Rewards)
        titles = textConstants.REDEMPTION_FAILED_TITLES;
      // Check for CardDeclined with custom backend messages
      if (paymentError.value.type === Payment.Card) {
        const cardDeclined = paymentError.value.value;
        if (cardDeclined.messageTitle && cardDeclined.messageBody) {
          titles = {
            icon: textConstants.ERROR_ICON,
            title: cardDeclined.messageTitle,
            subtitle: cardDeclined.messageBody,
            primaryButtonText: textConstants.EDIT_PAYMENT_INFO,
          };
        }
      }
      break;
    // ErrorCode and ProductError envelopes resolve to the same `code` space and
    // identical copy, so they share one body (QuoteExpired → CT1261T-419 lives
    // here once, not per-envelope).
    case PaymentErrorEnum.ErrorCode:
    case PurchaseErrorEnum.ProductError:
      agentSubtitle = getAgentErrorSubtitle(code);
      agentTitle = getAgentErrorTitle(code);

      switch (code) {
        case PaymentErrorEnum.UserCardNotFound:
          titles = textConstants.USER_CARD_ERROR_TITLES;
          break;
        case PaymentErrorEnum.FraudAutoReject:
        case PaymentErrorEnum.LikelyFraud:
          titles = textConstants.FRAUD_TITLES();
          break;
        case HotelBookErrorType.CheckInMinimumAgeNotMet:
          titles = textConstants.CHECK_IN_AGE_NOT_MET({
            minimumAge: checkInMinimumAge,
          });
          break;
        case HotelBookErrorType.PaymentError:
        case HotelBookErrorType.ProviderPaymentError:
          titles = getPaymentErrorTitles();
          break;
        case HotelBookErrorType.LackOfInventory:
        case HotelBookErrorType.RateNotAvailable:
        case HotelBookErrorType.RateSoldOut:
        case HotelBookErrorType.RoomSoldOut:
        case HotelBookErrorType.RateChanged: {
          handleNoAvailability(code);
          break;
        }
        case LodgingErrorEnum.QuoteExpired:
          // CT1261T-419: retryable connection/quote-expired modal (flag-gated).
          if (isLodgingErrorCopyUpdateEnabled) {
            titles = textConstants.CONNECTION_ERROR_TITLES;
            isConnection = true;
          }
          break;
        case PaymentErrorEnum.RedemptionFailure:
          titles = textConstants.REDEMPTION_FAILED_TITLES;
          break;
      }
      break;
    default:
  }

  return { ...titles, agentSubtitle, agentTitle, isConnection };
};

// Mirrors the codes routed to LACK_OF_INVENTORY_TITLES /
// RATE_NOT_AVAILABLE_TITLES in `handleNoAvailability` above.
const UNAVAILABLE_HOTEL_BOOK_ERROR_CODES: string[] = [
  HotelBookErrorType.LackOfInventory,
  HotelBookErrorType.RateSoldOut,
  HotelBookErrorType.RoomSoldOut,
  HotelBookErrorType.RateNotAvailable,
  HotelBookErrorType.RateChanged,
];

// Confirmation-details errors take priority over price-quote errors, matching
// `getErrorTitles`.
export const getIsHotelUnavailableError = createSelector(
  getHasError,
  getConfirmationDetailsError,
  getPriceQuoteErrors,
  (hasError, confirmationDetailsErrors, priceQuoteErrors): boolean => {
    if (!hasError) return false;

    const error = confirmationDetailsErrors[0] ?? priceQuoteErrors[0];
    if (error == null) return false;

    const code = getHotelBookErrorCode(error);
    return code != null && UNAVAILABLE_HOTEL_BOOK_ERROR_CODES.includes(code);
  }
);

export const getScheduleBookCallState = (state: IStoreState) =>
  state.hotelBook.scheduleBookCallState;

const isSelectedHtsCfarOffer = (
  selectedCfarId: ReturnType<typeof selectedCfarIdSelector>
): selectedCfarId is NonNullable<ReturnType<typeof selectedCfarIdSelector>> =>
  selectedCfarId != null &&
  selectedCfarId.value !== DO_NOT_APPLY_OPTION_KEY &&
  selectedCfarId.offerType === CfarOfferType.Hts;

export const getHtsAncillaryIds = createSelector(
  selectedCfarIdSelector,
  (selectedCfarId) => {
    const htsAncillaryIds: HtsAncillaryId[] = [];
    if (isSelectedHtsCfarOffer(selectedCfarId)) {
      htsAncillaryIds.push({
        offerId: selectedCfarId.value,
        HtsAncillaryId: HtsAncillaryIdType.HtsCfarId,
      });
    }
    return htsAncillaryIds;
  }
);

export const priceQuoteParamsSelector = createSelector(
  getFromDateInBook,
  getUntilDateInBook,
  getHotelBookChosenRoomInfo,
  getHotelBookChosenProduct,
  getUserSelectedTravelerId,
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getHotelBookSelectedAvailability,
  getAgentEmail,
  selectedCfarIdSelector,
  getUserSelectedTravelerIds,
  (
    fromDate,
    untilDate,
    roomInfo,
    product,
    personId,
    emailAddress,
    phoneNumber,
    lodging,
    agentEmail,
    selectedCfarId,
    userSelectedTravelerIds
  ): HotelPriceQuoteScheduleRequestV2 | null => {
    const primaryTravelerId = personId ?? userSelectedTravelerIds[0];
    const additionalGuests = userSelectedTravelerIds.slice(1);

    if (!product || !primaryTravelerId || !lodging) {
      return null;
    }

    const hotelQuoteRequest: HotelQuoteScheduleRequest = {
      token: "00000000-0000-0000-0000-000000000000",
      quoteRequest: product?.opaqueQuoteRequest,
      personId: primaryTravelerId,
      emailAddress: emailAddress || "",
      phoneNumber: phoneNumber || "",
      dates:
        fromDate && untilDate
          ? {
              from: dayjs(fromDate).format("YYYY-MM-DD"),
              until: dayjs(untilDate).format("YYYY-MM-DD"),
            }
          : undefined,
      id: lodging.lodging.id,
      name: lodging.lodging.name,
      city: lodging.lodging.city,
      country: lodging.lodging.country,
      starRating: lodging.lodging.starRating,
      providerName: lodging.price?.providerName,
      bedTypes: roomInfo?.roomInfo.beds,
      lodgingCollection: LodgingCollectionEnum.NoCollection,
      additionalGuests,
    };
    if (agentEmail) {
      hotelQuoteRequest.delegatedTo = agentEmail;
    }

    const isOfferValid =
      selectedCfarId != null &&
      selectedCfarId.value !== DO_NOT_APPLY_OPTION_KEY;
    const isHtsOffer = isOfferValid && isSelectedHtsCfarOffer(selectedCfarId);
    const isNonHtsCfarOffer = isOfferValid && !isHtsOffer;

    const hotelAncillaryIds = {
      encryptedCfarId: isNonHtsCfarOffer ? selectedCfarId?.value : undefined,
      encryptedHtsCfarOfferToken: isHtsOffer
        ? selectedCfarId?.value
        : undefined,
    };

    return {
      hotelQuoteRequest,
      hotelAncillaryIds,
    };
  }
);

export const bookingInProgressSelector = createSelector(
  getScheduleBookCallState,
  getConfirmationDetailsCallState,
  (scheduleBookCallState, confirmationCallState) =>
    scheduleBookCallState === CallState.InProcess ||
    confirmationCallState === CallState.InProcess
);

export const hasNoUserPassengersSelector = createSelector(
  getUserPassengers,
  getUserPassengerCallState,
  (userPassengers, userPassengerCallState) =>
    userPassengers.length === 0 && userPassengerCallState === CallState.Success
);

// PAYMENT

export const getRewardsPaymentAccountReferenceId = (state: IStoreState) =>
  state.hotelBook.rewardsAccountReferenceId;

export const getRewardsPaymentAccount = createSelector(
  getRewardsPaymentAccountReferenceId,
  getRewardsAccounts,
  (accountId, rewardsAccounts) =>
    rewardsAccounts.find((account) => account.accountReferenceId === accountId)
);

export const getRewardsPaymentInFiatCurrency = (state: IStoreState) =>
  state.hotelBook.rewardsPaymentInFiatCurrency;

export const getRewardsPaymentInRewardsCurrency = (state: IStoreState) =>
  state.hotelBook.rewardsPaymentTotal;

export const getRewardsRemainingAfterPurchase = createSelector(
  getRewardsPaymentAccount,
  getRewardsPaymentInFiatCurrency,
  (paymentAccount, rewardsPaymentAmount) => {
    if (!paymentAccount) return "";

    const {
      rewardsCashEquivalent: { value, currencyCode, currencySymbol },
    } = paymentAccount;

    const remainder = value - (rewardsPaymentAmount?.value || 0);

    return getTotalPriceText({
      price: {
        currencyCode,
        currencySymbol,
        value: remainder,
      },
      priceFormatter: twoDecimalFormatter,
    });
  }
);

export const getTravelWalletOfferToApplyAmount = createSelector(
  getTravelWalletItemsToApply,
  (travelWalletItemsToApply) => {
    if (travelWalletItemsToApply.offerToApply) {
      return travelWalletItemsToApply.offerToApply.amount.amount * -1;
    }
    return 0;
  }
);

export const getTravelWalletCreditToApplyAmount = createSelector(
  getTravelWalletItemsToApply,
  getPricingEstimateTotal,
  getPurchasePriceQuotePricing,
  getTravelWalletOfferToApplyAmount,
  (
    travelWalletItemsToApply,
    estimate,
    purchasePriceQuotePricing,
    offerAppliedAmount
  ) => {
    const totalFiatValue: number =
      purchasePriceQuotePricing?.payNowTotal.fiat.value ||
      estimate?.fiat.value ||
      0;
    const totalFiatValueWithOfferApplied: number =
      totalFiatValue - offerAppliedAmount;
    if (
      travelWalletItemsToApply.creditToApply &&
      totalFiatValueWithOfferApplied > 0
    ) {
      const maxApplicableCredit =
        travelWalletItemsToApply.offerToApply?.maxApplicableCredit?.amount;
      const amount =
        maxApplicableCredit &&
        maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount
          ? maxApplicableCredit
          : travelWalletItemsToApply.creditToApply.amount.amount;
      if (totalFiatValueWithOfferApplied < amount * -1) {
        if (
          maxApplicableCredit &&
          maxApplicableCredit <
            travelWalletItemsToApply.creditToApply.amount.amount &&
          travelWalletItemsToApply.creditToApply.amount.amount * -1 <
            totalFiatValueWithOfferApplied
        ) {
          return travelWalletItemsToApply.creditToApply.amount.amount * -1;
        }
        return totalFiatValueWithOfferApplied;
      }
      if (
        maxApplicableCredit &&
        maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount
      ) {
        return travelWalletItemsToApply.creditToApply.amount.amount * -1;
      }
      return amount * -1;
    }
    return 0;
  }
);

export const getMaxApplicableTravelWalletCreditOnlyAmount = createSelector(
  getApplicableCredit,
  getPricingEstimateTotal,
  getPurchasePriceQuotePricing,
  (credit, estimate, purchasePriceQuotePricing): number => {
    if (purchasePriceQuotePricing && credit) {
      if (
        purchasePriceQuotePricing.payNowTotal.fiat.value <
        credit.amount.amount * -1
      ) {
        return purchasePriceQuotePricing.payNowTotal.fiat.value * -1;
      }
      return credit.amount.amount;
    }
    if (estimate && credit) {
      const totalFiatValue = estimate?.fiat.value + credit?.amount.amount;
      if (totalFiatValue < 0) {
        return estimate.fiat.value * -1;
      }
      return credit.amount.amount;
    }
    return 0;
  }
);

export const getMaxApplicableTravelWalletCreditAmount = createSelector(
  getApplicableCredit,
  getPricingEstimateTotal,
  getPurchasePriceQuotePricing,
  getTravelWalletOfferToApplyAmount,
  (credit, estimate, purchasePriceQuotePricing, offerAppliedAmount): number => {
    if (purchasePriceQuotePricing && credit) {
      if (
        purchasePriceQuotePricing.payNowTotal.fiat.value - offerAppliedAmount <
        credit.amount.amount * -1
      ) {
        return (
          (purchasePriceQuotePricing.payNowTotal.fiat.value -
            offerAppliedAmount) *
          -1
        );
      }
      return credit.amount.amount;
    }
    if (estimate && credit) {
      const totalFiatValue =
        estimate?.fiat.value - offerAppliedAmount + credit?.amount.amount;
      if (totalFiatValue < 0) {
        return (estimate.fiat.value - offerAppliedAmount) * -1;
      }
      return credit.amount.amount;
    }
    return 0;
  }
);

export const getTotalToPayWithoutTravelWalletItems = createSelector(
  getPricingEstimateTotal,
  getPurchasePriceQuotePricing,
  getSelectedAccountReferenceIdIfRedemptionEnabled,
  (
    estimate,
    purchasePriceQuotePricing,
    selectedAccountReferenceId
  ):
    | {
        fiat: FiatPrice;
        reward: RewardsPrice | undefined;
      }
    | undefined => {
    const payNowTotal = purchasePriceQuotePricing?.payNowTotal ?? estimate;

    if (!payNowTotal) {
      return undefined;
    }

    return {
      fiat: payNowTotal.fiat,
      reward: selectedAccountReferenceId
        ? payNowTotal.rewards[selectedAccountReferenceId]
        : undefined,
    };
  }
);

export const getTotalToPay = createSelector(
  getPricingEstimateTotal,
  getTravelWalletItemsToApply,
  getPurchasePriceQuotePricing,
  (
    estimate,
    travelWalletItemsToApply,
    purchasePriceQuotePricing
  ): Prices | null => {
    const total = purchasePriceQuotePricing
      ? purchasePriceQuotePricing.payNowTotal
      : estimate;

    const rewardsTotal = {};
    if (
      total &&
      (travelWalletItemsToApply.offerToApply ||
        travelWalletItemsToApply.creditToApply)
    ) {
      let walletItemsTotal: number =
        travelWalletItemsToApply.offerToApply?.amount.amount || 0;

      if (travelWalletItemsToApply.creditToApply) {
        const maxApplicableCredit =
          travelWalletItemsToApply.offerToApply?.maxApplicableCredit?.amount;
        if (
          maxApplicableCredit &&
          maxApplicableCredit >
            travelWalletItemsToApply.creditToApply.amount.amount
        ) {
          walletItemsTotal += maxApplicableCredit;
        } else {
          walletItemsTotal +=
            travelWalletItemsToApply.creditToApply.amount.amount;
        }
      }
      Object.keys(total.rewards).forEach((accountId) => {
        if (total) {
          let rewardTotalValue = total.rewards[accountId].value;
          rewardTotalValue =
            total.rewards[accountId].currency === "Cash"
              ? rewardTotalValue + walletItemsTotal
              : rewardTotalValue + walletItemsTotal * 100;
          rewardsTotal[accountId] = {
            currency: total.rewards[accountId].currency,
            currencyDescription: total.rewards[accountId].currencyDescription,
            value: rewardTotalValue > 0 ? rewardTotalValue : 0,
          };
        }
      });
      return {
        fiat: {
          currencyCode: total.fiat.currencyCode,
          currencySymbol: total.fiat.currencySymbol,
          value:
            total.fiat.value + walletItemsTotal > 0
              ? total.fiat.value + walletItemsTotal
              : 0,
        },
        rewards: rewardsTotal,
      };
    }
    return purchasePriceQuotePricing
      ? purchasePriceQuotePricing.payNowTotal
      : estimate;
  }
);

export const getIsTravelWalletCreditPaymentOnly = createSelector(
  getTotalToPay,
  getTravelWalletItemsToApply,
  (total, travelWalletItemsToApply) => {
    if (
      (!travelWalletItemsToApply.offerToApply ||
        getHSPEarnOfferDescription(travelWalletItemsToApply.offerToApply)) &&
      travelWalletItemsToApply.creditToApply &&
      total?.fiat.value === 0
    ) {
      return true;
    }
    return false;
  }
);

export const getIsTravelWalletOfferPaymentOnly = createSelector(
  getTotalToPay,
  getTravelWalletItemsToApply,
  (total, travelWalletItemsToApply) => {
    if (
      travelWalletItemsToApply.offerToApply &&
      !getHSPEarnOfferDescription(travelWalletItemsToApply.offerToApply) &&
      !travelWalletItemsToApply.creditToApply &&
      total?.fiat.value === 0
    ) {
      return true;
    }
    return false;
  }
);

export const getIsStackedTravelWalletPaymentOnly = createSelector(
  getTotalToPay,
  getTravelWalletItemsToApply,
  (total, travelWalletItemsToApply) => {
    if (
      travelWalletItemsToApply.offerToApply &&
      !getHSPEarnOfferDescription(travelWalletItemsToApply.offerToApply) &&
      travelWalletItemsToApply.creditToApply &&
      total?.fiat.value === 0
    ) {
      return true;
    }
    return false;
  }
);

export const getIsTravelWalletPaymentOnly = createSelector(
  getIsTravelWalletCreditPaymentOnly,
  getIsTravelWalletOfferPaymentOnly,
  getIsStackedTravelWalletPaymentOnly,
  (
    isTravelWalletCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly
  ) => {
    if (
      isTravelWalletCreditPaymentOnly ||
      isTravelWalletOfferPaymentOnly ||
      isStackedTravelWalletPaymentOnly
    ) {
      return true;
    }
    return false;
  }
);

export const getIsCreditCardPaymentRequired = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  getIsTravelWalletPaymentOnly,
  getIsStackedTravelWalletPaymentOnly,
  (
    total,
    rewardsPaymentAmount,
    isTravelWalletPaymentOnly,
    isStackedTravelWalletPaymentOnly
  ) => {
    if (isTravelWalletPaymentOnly || isStackedTravelWalletPaymentOnly)
      return false;

    if (!total) return true;
    if (!rewardsPaymentAmount) return true;

    return total.fiat.value > rewardsPaymentAmount.value;
  }
);

export const getTotalCreditCardPaymentRequired = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  (estimate, rewardsPaymentAmount) => {
    if (!estimate) return "";

    if (!rewardsPaymentAmount) {
      return getTotalPriceText({
        price: estimate.fiat,
        priceFormatter: twoDecimalFormatter,
      });
    }

    const remainder = estimate.fiat.value - rewardsPaymentAmount.value;
    const total = remainder <= 0 ? 0 : remainder;

    return getTotalPriceText({
      price: {
        currencyCode: estimate.fiat.currencyCode,
        currencySymbol: estimate.fiat.currencySymbol,
        value: total,
      },
      priceFormatter: twoDecimalFormatter,
    });
  }
);

export const getTotalCreditCardPaymentRequiredNumber = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  (estimate, rewardsPaymentAmount) => {
    if (!estimate) return null;

    if (!rewardsPaymentAmount) {
      return estimate.fiat.value;
    }

    const remainder = estimate.fiat.value - rewardsPaymentAmount.value;
    const total =
      remainder <= 0 ? 0 : Math.round((remainder + Number.EPSILON) * 100) / 100;
    return total;
  }
);

export const getTotalCreditCardPaymentRequiredInFiatPrice = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  (totalToPay, rewardsPaymentAmount): FiatPrice | undefined => {
    if (!totalToPay) return undefined;

    if (!rewardsPaymentAmount) return totalToPay.fiat;

    const remainder = totalToPay.fiat.value - rewardsPaymentAmount.value;
    const total = remainder <= 0 ? 0 : remainder;

    return {
      currencyCode: totalToPay.fiat.currencyCode,
      currencySymbol: totalToPay.fiat.currencySymbol,
      value: total,
    };
  }
);

export const getAddedAncillaryLineItems = createSelector(
  getAddedAncillariesPricingInCheckout,
  (addedAncillariesPricing): IHotelPriceLineItem[] =>
    addedAncillariesPricing
      .filter(
        (addOn) =>
          addOn.kind !== AncillaryKindEnum.Cfar ||
          (addOn.kind === AncillaryKindEnum.Cfar &&
            addOn.premium.fiat.value > 0)
      )
      .map((pricing) => {
        switch (pricing.kind) {
          case AncillaryKindEnum.Cfar:
            return {
              icon: "check-shield-blue",
              title: textConstants.CANCEL_FOR_ANY_REASON,
              value: getTotalPriceText({
                price: pricing.premium.fiat,
                priceFormatter: twoDecimalFormatter,
              }),
              product: pricing.kind.toLowerCase(),
            };
          default:
            return [];
        }
      })
      .flat()
);

export const hasAddOnBeenAttachedSelector = createSelector(
  getAddedAncillaryLineItems,
  (addedAncillaryLineItems): boolean => addedAncillaryLineItems.length > 0
);

export const getPaymentMethodRewardsAccountId = (state: IStoreState) =>
  state.hotelBook.paymentMethodRewardsAccountId;

export const getHotelPricingLineItems = createSelector(
  getSearchedNightCountInBook,
  getRoomsCountInBook,
  getHotelBookChosenProduct,
  getPriceQuote,
  getRewardsPaymentAccount,
  getPaymentMethodRewardsAccountId,
  getRewardsAccounts,
  getAddedAncillaryLineItems,
  getAddedAncillariesPricingInCheckout,
  getPurchasePriceQuotePricing,
  cfarNegativePremiumDiscountSelector,
  (
    nights,
    roomsCount,
    product,
    priceQuote,
    rewardsPaymentAccount,
    paymentMethodRewardsAccountId,
    rewardsAccounts,
    addedAncillaryLineItems,
    addedAncillaryPricing,
    purchasePriceQuotePricing,
    cfarNegativePremiumDiscount
  ): IHotelPriceLineItem[][] => {
    const { payNowTotal, hotelPricing } = purchasePriceQuotePricing ?? {};

    const cfarNegativePremiumDiscountValue =
      cfarNegativePremiumDiscount?.fiat.value ?? 0;

    const getTotalBreakdown = ({
      subtotalFiat,
      taxesFiat,
    }: {
      subtotalFiat: FiatPrice;
      taxesFiat: FiatPrice;
    }): IHotelPriceLineItem[] => {
      const items = [
        {
          title: textConstants.ROOM_TOTAL({
            nights,
            roomsCount,
          }),
          value: getTotalPriceText({
            price: subtotalFiat,
            priceFormatter: twoDecimalFormatter,
          }),
        } as IHotelPriceLineItem,
        {
          title: textConstants.TAXES_AND_FEES,
          value: getTotalPriceText({
            price: taxesFiat,
            priceFormatter: twoDecimalFormatter,
          }),
          tooltip: textConstants.TAXES_AND_FEES_TOOLTIP,
        } as IHotelPriceLineItem,
      ];

      return items.concat(addedAncillaryLineItems);
    };

    if (priceQuote && payNowTotal && hotelPricing && product) {
      const rewardsAccountId = rewardsPaymentAccount
        ? rewardsPaymentAccount.accountReferenceId
        : paymentMethodRewardsAccountId ||
          Object.keys(product.total.rewards)[0];
      const includeRewards =
        rewardsAccounts.find(
          (account) => account.accountReferenceId === rewardsAccountId
        )?.allowRewardsRedemption ?? true;

      return [
        getTotalBreakdown({
          subtotalFiat: applyDiscountValueToFiatPrice(
            hotelPricing?.subtotal.fiat,
            [cfarNegativePremiumDiscountValue]
          ),
          taxesFiat: hotelPricing?.taxes.fiat,
        }),
        [
          {
            title: textConstants.DUE_TODAY,

            value: getTotalPriceText({
              price: payNowTotal.fiat,
              priceFormatter: twoDecimalFormatter,
            }),
            rewardsValue: includeRewards
              ? getRewardsString(payNowTotal.rewards[rewardsAccountId])
              : undefined,
            boldLabel: true,
            type: "due-today",
          } as IHotelPriceLineItem,
          ...(hotelPricing.feeBreakdown.total
            ? [
                {
                  title: textConstants.ADDITIONAL_FEES,
                  value: getTotalPriceText({
                    price: hotelPricing.feeBreakdown.total,
                    priceFormatter: twoDecimalFormatter,
                  }),
                  type: "additional-fees",
                  tooltip: textConstants.ADDITIONAL_FEES_TOOLTIP,
                  boldValue: false,
                } as IHotelPriceLineItem,
              ]
            : []),
          ...(hotelPricing.feeBreakdown.total
            ? [
                {
                  title: textConstants.TOTAL,
                  value: getTotalPriceText({
                    price: hotelPricing.tripTotal.fiat,
                    priceFormatter: twoDecimalFormatter,
                  }),
                  boldLabel: true,
                  type: "total",
                } as IHotelPriceLineItem,
              ]
            : []),
        ],
      ];
    }
    if (product) {
      const rewardsAccount = rewardsPaymentAccount
        ? rewardsPaymentAccount.accountReferenceId
        : Object.keys(product.total.rewards)[0];
      const includeRewards =
        rewardsAccounts.find(
          (account) => account.accountReferenceId === rewardsAccount
        )?.allowRewardsRedemption ?? true;
      const dueTodayTotal = getSubtotalWithAncillariesLegacy(
        product.total,
        addedAncillaryPricing
      );
      const total = getSubtotalWithAncillariesLegacy(
        product.tripTotal,
        addedAncillaryPricing
      );

      return [
        getTotalBreakdown({
          subtotalFiat: applyDiscountValueToFiatPrice(product.sellRate.fiat, [
            cfarNegativePremiumDiscountValue,
          ]),
          taxesFiat: {
            value: Object.keys(product.taxBreakdown).reduce(
              (sum, key) => product.taxBreakdown[key].fiat.value + sum,
              0
            ),
            currencyCode: product.sellRate.fiat.currencyCode,
            currencySymbol: product.sellRate.fiat.currencySymbol,
          },
        }),
        [
          {
            title: textConstants.DUE_TODAY,
            value: getTotalPriceText({
              price: dueTodayTotal.fiat,
              priceFormatter: twoDecimalFormatter,
            }),
            rewardsValue: includeRewards
              ? getRewardsString(dueTodayTotal.rewards[rewardsAccount])
              : undefined,
            boldLabel: true,
            type: "due-today",
          } as IHotelPriceLineItem,
          ...(product.feeBreakdown.total
            ? [
                {
                  title: textConstants.ADDITIONAL_FEES,
                  value: getTotalPriceText({
                    price: product.feeBreakdown.total,
                    priceFormatter: twoDecimalFormatter,
                  }),
                  type: "additional-fees",
                  tooltip: textConstants.ADDITIONAL_FEES_TOOLTIP,
                  boldValue: false,
                } as IHotelPriceLineItem,
              ]
            : []),
          ...(product.feeBreakdown.total
            ? [
                {
                  title: textConstants.TOTAL,
                  value: getTotalPriceText({
                    price: total.fiat,
                    priceFormatter: twoDecimalFormatter,
                  }),
                  boldLabel: true,
                  type: "total",
                } as IHotelPriceLineItem,
              ]
            : []),
        ],
      ];
    }
    return [];
  }
);

export const getHotelTripTotalInPrices = createSelector(
  getPriceQuote,
  getHotelBookChosenProduct,
  getRewardsPaymentAccount,
  getRewardsAccountWithLargestValue,
  getPurchasePriceQuotePricing,
  (
    priceQuote,
    chosenProduct,
    rewardsPaymentAccount,
    rewardsAccountWithLargestValue,
    purchasePriceQuotePricing
  ): { fiat: FiatPrice; rewards?: RewardsPrice } | null => {
    const { hotelPricing } = purchasePriceQuotePricing ?? {};
    const activeRewardsAccount =
      rewardsPaymentAccount ?? rewardsAccountWithLargestValue;
    const includeRewards = activeRewardsAccount?.allowRewardsRedemption ?? true;

    if (priceQuote && hotelPricing && activeRewardsAccount) {
      return {
        fiat: hotelPricing.tripTotal.fiat,
        rewards: includeRewards
          ? hotelPricing.tripTotal.rewards[
              activeRewardsAccount.accountReferenceId
            ]
          : undefined,
      };
    }
    if (chosenProduct) {
      return {
        fiat: chosenProduct.tripTotal.fiat,
        rewards: includeRewards
          ? chosenProduct.tripTotal.rewards[
              activeRewardsAccount?.accountReferenceId ??
                Object.keys(chosenProduct.total.rewards)[0]
            ]
          : undefined,
      };
    }
    return null;
  }
);

export const getHotelRewardsAndTotalLineItems = createSelector(
  getRewardsPaymentAccount,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getTotalCreditCardPaymentRequired,
  getSelectedPaymentMethodId,
  getPaymentMethods,
  getTravelWalletItemsToApply,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletCreditToApplyAmount,
  (
    rewardsPaymentAccount,
    rewardsPayment,
    rewardsInFiat,
    totalAmountDueOnCredit,
    paymentMethodId,
    paymentMethods,
    travelWalletItemsToApply,
    offerAmountToApply,
    creditAmountToApply
  ) => {
    const rewardsLineItems = [];

    if (
      rewardsPaymentAccount &&
      (rewardsPaymentAccount.allowRewardsRedemption ?? true)
    ) {
      const fiatPrice = rewardsInFiat ?? {
        currencyCode: rewardsPaymentAccount?.rewardsCashEquivalent.currencyCode,
        currencySymbol:
          rewardsPaymentAccount?.rewardsCashEquivalent.currencySymbol,
        value: 0,
      };

      const priceText = getTotalPriceText({
        price: {
          ...fiatPrice,
          value: fiatPrice.value,
        },
        priceFormatter: twoDecimalFormatter,
      });

      const rewardsText = rewardsPayment
        ? `- ${getRewardsString(rewardsPayment)}`
        : "";
      rewardsLineItems.push({
        title: `${rewardsPaymentAccount?.productDisplayName} ${textConstants.REWARDS}`,
        value: `- ${priceText}`,
        rewardsValue: rewardsText,
      });
    }

    const paymentMethod = paymentMethods.find((p) => p.id === paymentMethodId);

    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply
    ) {
      const travelWalletItems = [];

      const isHspEarnOffer = !!getHSPEarnOfferDescription(
        travelWalletItemsToApply.offerToApply
      );
      if (
        travelWalletItemsToApply.offerToApply?.amount &&
        (offerAmountToApply || isHspEarnOffer)
      ) {
        travelWalletItems.push({
          title: textConstants.TRAVEL_OFFER_APPLIED,
          value: offerAmountToApply
            ? getTotalPriceText({
                price: {
                  value: offerAmountToApply * -1,
                  currencyCode:
                    travelWalletItemsToApply.offerToApply?.amount.currency,
                  currencySymbol: CurrencyFormatters.getSymbol(
                    travelWalletItemsToApply.offerToApply?.amount.currency
                  ),
                },
                priceFormatter: twoDecimalFormatter,
              })
            : "",
          isTravelOffer: !!travelWalletItemsToApply.offerToApply,
          icon: "offer-tag",
        });
      }
      if (travelWalletItemsToApply.creditToApply && creditAmountToApply) {
        const breakdownHasStatementCredit =
          !!travelWalletItemsToApply.creditToApply.breakdown?.some(
            (detail) =>
              detail.CreditDetail === "Statement" &&
              Math.abs(detail.usableAmount.amount) > 0
          );
        travelWalletItems.push({
          title: breakdownHasStatementCredit
            ? textConstants.TOTAL_TRAVEL_CREDITS_APPLIED
            : textConstants.TRAVEL_CREDITS_APPLIED,
          value: getTotalPriceText({
            price: {
              value: creditAmountToApply * -1,
              currencyCode:
                travelWalletItemsToApply.creditToApply?.amount.currency,
              currencySymbol: CurrencyFormatters.getSymbol(
                travelWalletItemsToApply.creditToApply?.amount.currency
              ),
            },
            priceFormatter: twoDecimalFormatter,
          }),
          isTravelCredit: !!travelWalletItemsToApply.creditToApply,
          icon: "piggy-bank-icon",
          travelCreditBreakdown: breakdownHasStatementCredit
            ? getCheckoutCreditBreakdown(
                travelWalletItemsToApply.creditToApply.breakdown || [],
                creditAmountToApply,
                travelWalletItemsToApply.creditToApply.amount.currency
              )
            : [],
        });
      }

      return [
        ...travelWalletItems,
        ...rewardsLineItems,
        {
          title: paymentMethod
            ? `Ending in ${paymentMethod.last4}:`
            : textConstants.AMOUNT_DUE,
          value: totalAmountDueOnCredit,
          icon: IconName.Payment,
        },
      ];
    }

    return [
      ...rewardsLineItems,
      {
        title: paymentMethod
          ? `Ending in ${paymentMethod.last4}:`
          : textConstants.AMOUNT_DUE,
        value: totalAmountDueOnCredit,
        icon: IconName.Payment,
      },
    ];
  }
);

export enum Progress {
  NOT_STARTED = 0,
  IN_PROGRESS = 1,
  COMPLETED = 2,
}

export const getCombinedBookingSteps = createSelector(
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getUserSelectedTravelerId,
  getSelectedPaymentMethodId,
  getRewardsPaymentAccountReferenceId,
  getIsCreditCardPaymentRequired,
  getAllowRewardsWithPolicy,
  getUserSelectedTravelerIds,
  (
    email,
    phoneNumber,
    traveler,
    selectedPayment,
    selectedRewardsAccount,
    creditCardRequired,
    canRedeemRewards,
    userSelectedTravelerIds
  ) => {
    const paymentCompleted = () => {
      const unsettledPayment = !selectedPayment && creditCardRequired;

      if (selectedRewardsAccount && unsettledPayment) {
        return Progress.IN_PROGRESS;
      }
      if (!selectedRewardsAccount && !selectedPayment) {
        return Progress.NOT_STARTED;
      }
      return Progress.COMPLETED;
    };
    return [
      {
        name: textConstants.TRAVELER_INFORMATION,
        status:
          (!!traveler || userSelectedTravelerIds.length > 0) &&
          email &&
          phoneNumber &&
          emailRegex.test(email) &&
          phoneRegex.test(phoneNumber)
            ? Progress.COMPLETED
            : Progress.IN_PROGRESS,
      },
      {
        name: canRedeemRewards
          ? textConstants.REWARDS_AND_PAYMENT
          : textConstants.REWARDS_AND_PAYMENT_SHORTENED,
        status: paymentCompleted(),
      },
    ];
  }
);

export const getBookingProgress = createSelector(
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getUserSelectedTravelerId,
  getSelectedPaymentMethodId,
  getRewardsPaymentAccountReferenceId,
  getIsCreditCardPaymentRequired,
  getAllowRewardsWithPolicy,
  getUserSelectedTravelerIds,
  (
    email,
    phoneNumber,
    traveler,
    selectedPayment,
    selectedRewardsAccount,
    creditCardRequired,
    canRedeemRewards,
    userSelectedTravelerIds
  ) => {
    const paymentCompleted = () => {
      const unsettledPayment = !selectedPayment && creditCardRequired;

      if (selectedRewardsAccount && unsettledPayment) {
        return Progress.IN_PROGRESS;
      }
      if (!selectedRewardsAccount && !selectedPayment) {
        return Progress.NOT_STARTED;
      }
      return Progress.COMPLETED;
    };
    return [
      {
        name: textConstants.ADD_TRAVELERS,
        status:
          !!traveler || userSelectedTravelerIds.length > 0
            ? Progress.COMPLETED
            : Progress.IN_PROGRESS,
      },
      {
        name: textConstants.CONTACT,
        status:
          email &&
          phoneNumber &&
          emailRegex.test(email) &&
          phoneRegex.test(phoneNumber)
            ? Progress.COMPLETED
            : Progress.NOT_STARTED,
      },
      {
        name: canRedeemRewards
          ? textConstants.REWARDS_AND_PAYMENT
          : textConstants.REWARDS_AND_PAYMENT_SHORTENED,
        status: paymentCompleted(),
      },
    ];
  }
);
export const getBookingInProgress = createSelector(
  getSchedulePriceQuoteCallState,
  getPollPriceQuoteCallState,
  getScheduleBookCallState,
  getConfirmationDetailsCallState,
  (scheduleQuote, pollPriceQuote, scheduleBook, confirmDetails) =>
    scheduleQuote === CallState.InProcess ||
    pollPriceQuote === CallState.InProcess ||
    scheduleBook === CallState.InProcess ||
    confirmDetails === CallState.InProcess
);

// CT1261T-421: drives the non-blocking inline decrease notice. Shown only when
// the decrease is the sole thing on screen — not while a blocking error modal
// (getHasError) or a requote success banner owns the same sticky slot, and not
// while quote/book/confirmation processing is active (the BookingInProgress
// LoadingPopup, which the sticky notice would otherwise paint over on mobile).
export const getShowPriceDecreaseMessage = createSelector(
  getPriceDifference,
  isLodgingErrorCopyUpdateEnabledSelector,
  getHasError,
  getShowRequoteSuccessBanner,
  getBookingInProgress,
  (
    priceDifference,
    isLodgingErrorCopyUpdateEnabled,
    hasError,
    showRequoteSuccessBanner,
    bookingInProgress
  ): boolean =>
    priceDifference.hasDifference &&
    !isPriceDifferenceBlocking(
      priceDifference,
      isLodgingErrorCopyUpdateEnabled
    ) &&
    !hasError &&
    !showRequoteSuccessBanner &&
    !bookingInProgress
);

export const getSelectedRoomPolicyCompliance = createSelector(
  getHotelShopChosenRoomInfo,
  (roomInfoProduct) =>
    roomInfoProduct && "corporateTravel" in roomInfoProduct.roomInfo
      ? roomInfoProduct.roomInfo?.corporateTravel.policyCompliance
      : undefined
);

export const getIsBookingValid = createSelector(
  getPriceDifference,
  getPriceDifferenceAcknowledged,
  getIsCreditCardPaymentRequired,
  getSelectedPaymentMethodId,
  getUserSelectedTravelerId,
  getBookingInProgress,
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getIsTravelWalletPaymentOnly,
  getPollPriceQuoteCallState,
  getPriceQuoteErrors,
  hotelBookTypeSelector,
  getUserSelectedTravelerIds,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    priceDifference,
    priceDifferenceAcknowledged,
    creditCardRequired,
    selectedPaymentMethodId,
    userSelectedTravelerId,
    bookingInProgress,
    email,
    phone,
    isTravelWalletPaymentOnly,
    priceQuoteCallState,
    priceQuoteErrors,
    hotelBookType,
    userSelectedTravelerIds,
    isLodgingErrorCopyUpdateEnabled
  ) => {
    const paymentUnsettled =
      creditCardRequired &&
      !selectedPaymentMethodId &&
      !isTravelWalletPaymentOnly;

    const isContactInfoReady = (): boolean => {
      switch (hotelBookType) {
        case HotelBookType.DEFAULT:
        default:
          return (
            (!!userSelectedTravelerId || userSelectedTravelerIds.length > 0) &&
            !!email &&
            !!phone
          );
      }
    };

    if (
      // CT1261T-421: a non-blocking decrease must not block booking.
      (priceDifference.hasDifference &&
        !priceDifferenceAcknowledged &&
        isPriceDifferenceBlocking(
          priceDifference,
          isLodgingErrorCopyUpdateEnabled
        )) ||
      priceQuoteErrors.length > 0 ||
      priceQuoteCallState !== CallState.Success ||
      paymentUnsettled ||
      bookingInProgress ||
      !isContactInfoReady()
    ) {
      return false;
    }
    return true;
  }
);

export const getEarn = (state: IStoreState) => state.hotelBook.productEarnValue;

const getPaymentRequestType = createSelector(
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  (
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    selectedPaymentMethodId
  ) => {
    if (selectedPaymentMethodId && !rewardsPayment) {
      return PaymentSplitRequestEnum.PaymentCardRequest;
    }
    if (
      rewardsAccountId &&
      rewardsPayment &&
      rewardsFiatPayment &&
      creditCardPayment?.value === 0
    ) {
      return PaymentSplitRequestEnum.PaymentRewardsRequest;
    }
    if (rewardsPayment && rewardsAccountId && rewardsFiatPayment) {
      return PaymentSplitRequestEnum.PaymentCardRewardsRequest;
    }
    return null;
  }
);

export const getCardPaymentRewardsAccount = createSelector(
  getPaymentMethodRewardsAccountId,
  getRewardsAccounts,
  (accountId, rewardsAccounts) =>
    rewardsAccounts.find((account) => account.accountReferenceId === accountId)
);

export const getEarnedString = createSelector(
  getEarn,
  getCardPaymentRewardsAccount,
  getAllowRewardsWithPolicy,
  (earn, account, canRedeemRewards) => {
    if (!earn || !account || !canRedeemRewards) return "";
    return textConstants.EARNED_STRING({
      ...account.rewardsBalance,
      value: earn,
    });
  }
);

export const getPaymentRequest = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getPaymentRequestType,
  getSession,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    session
  ): PaymentSplitRequest | null => {
    let amount: PaymentType | null = null;
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        const userCardPayment: UserCardPaymentType = {
          paymentId: paymentId || "",
          accountReferenceId,
          paymentAmount: {
            currency: creditCardPayment!.currencyCode,
            amount: roundToTwoDecimals(creditCardPayment!.value),
            PaymentAmount: PaymentAmountEnum.FiatAmount,
          },
          Payment: TypeOfPaymentEnum.UserCard,
        };
        amount = userCardPayment;
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        const splitPayment: SplitPaymentType = {
          paymentId: paymentId || "",
          accountReferenceId,
          paymentAmount: {
            fiatAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
              PaymentAmount: PaymentAmountEnum.FiatAmount,
            },
            rewardsAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
              PaymentAmount: PaymentAmountEnum.RewardsAmount,
            },
            PaymentAmount: PaymentAmountEnum.SplitAmount,
          },
          Payment: TypeOfPaymentEnum.Split,
        };
        amount = splitPayment;
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        const rewardsPaymentType: RewardsPaymentType = {
          paymentAmount: {
            rewardsAccountId: rewardsAccountId!,
            fiatValue: {
              amount: roundToTwoDecimals(rewardsFiatPayment!.value),
              currency: rewardsFiatPayment!.currencyCode,
            },
            rewardsPrice: {
              value: roundToTwoDecimals(rewardsPayment!.value),
              currency: rewardsPayment!.currency,
            },
            PaymentAmount: PaymentAmountEnum.RewardsAmount,
          },
          Payment: TypeOfPaymentEnum.Rewards,
        };
        amount = rewardsPaymentType;
        break;
      default:
        return null;
    }

    if (amount && session) {
      const request = {
        token: session,
        payment: amount,
        ancillaries: [],
      };
      return request;
    }
    return null;
  }
);

export const getOpaquePayments = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getPaymentRequestType,
  getTravelWalletItemsToApply,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletCreditToApplyAmount,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    travelWalletItemsToApply,
    offerToApplyAmount,
    creditToApplyAmount
  ): PaymentOpaqueValue[] | null => {
    const amount: PaymentOpaqueValue[] = [];
    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply
    ) {
      if (travelWalletItemsToApply.offerToApply) {
        const offer: PaymentOpaqueValue = {
          type: Payment.TravelWalletOffer,
          value: {
            offerId: travelWalletItemsToApply.offerToApply.id,
            description:
              travelWalletItemsToApply.offerToApply?.descriptions[0] || "",
            paymentAmount: {
              fiatValue: {
                amount: offerToApplyAmount,
                currency: travelWalletItemsToApply.offerToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletOffer,
          },
        };
        amount.push(offer);
      }
      if (travelWalletItemsToApply.creditToApply && creditToApplyAmount > 0) {
        const credit: PaymentOpaqueValue = {
          type: Payment.TravelWalletCredit,
          value: {
            offerId: travelWalletItemsToApply.creditToApply.id,
            description: "TravelWalletCredit", // creditToApply doesn't have a description
            paymentAmount: {
              fiatValue: {
                amount: roundToTwoDecimals(creditToApplyAmount),
                currency:
                  travelWalletItemsToApply.creditToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletCredit,
          },
        };
        amount.push(credit);
      }
    }
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        const userCardPayment: PaymentOpaqueValue = {
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          },
        };
        amount.push(userCardPayment);
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        const splitUserCardPayment: PaymentOpaqueValue = {
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          },
        };

        const splitRewardsPayment: PaymentOpaqueValue = {
          type: Payment.Rewards,
          value: {
            paymentAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
            },
            PaymentV2: PaymentV2Enum.Rewards,
          },
        };

        amount.push(splitUserCardPayment, splitRewardsPayment);
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        const rewardsPaymentType: PaymentOpaqueValue = {
          type: Payment.Rewards,
          value: {
            paymentAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
            },
            PaymentV2: PaymentV2Enum.Rewards,
          },
        };
        amount.push(rewardsPaymentType);
        break;
      default:
        break;
    }
    if (amount) {
      return amount;
    }
    return null;
  }
);

export const getCardPaymentAccount = createSelector(
  getRewardsAccounts,
  getPaymentRequest,
  (rewardsAccounts, paymentRequest: PaymentSplitRequest | null) => {
    switch (paymentRequest?.payment.Payment) {
      case TypeOfPaymentEnum.UserCard:
        return rewardsAccounts.find(
          (acc) =>
            acc.accountReferenceId ===
            (paymentRequest?.payment as UserCardPaymentType).accountReferenceId
        );
      case TypeOfPaymentEnum.Split:
        return rewardsAccounts.find(
          (acc) =>
            acc.accountReferenceId ===
            (paymentRequest?.payment as SplitPaymentType).accountReferenceId
        );
      default:
        return null;
    }
  }
);

export enum CheckoutSteps {
  ADD_TRAVELERS = 0,
  CONTACT = 1,
  REWARDS_AND_PAYMENT = 2,
}

export const isTravelerStepComplete = createSelector(
  getBookingProgress,
  (checkoutProgress) =>
    checkoutProgress[CheckoutSteps.ADD_TRAVELERS].status === Progress.COMPLETED
);

export const isContactStepComplete = createSelector(
  getBookingProgress,
  (checkoutProgress) =>
    checkoutProgress[CheckoutSteps.CONTACT].status === Progress.COMPLETED
);

export const areAllStepsCompletedInCheckout = createSelector(
  getBookingProgress,
  getIsTravelWalletPaymentOnly,
  (checkoutProgress, isTravelWalletPaymentOnly) =>
    checkoutProgress[CheckoutSteps.ADD_TRAVELERS].status ===
      Progress.COMPLETED &&
    checkoutProgress[CheckoutSteps.CONTACT].status === Progress.COMPLETED &&
    (!isTravelWalletPaymentOnly
      ? checkoutProgress[CheckoutSteps.REWARDS_AND_PAYMENT].status ===
        Progress.COMPLETED
      : true)
);

export const verifyPaymentMethodResultSelector = (state: IStoreState) =>
  state.hotelBook.verifyPaymentMethodResult;

export const getModalType = createSelector(
  getPriceQuoteErrors,
  getScheduleBookError,
  getSchedulePriceQuoteError,
  getConfirmationDetailsError,
  getPriceDifference,
  verifyPaymentMethodResultSelector,
  hasAddOnBeenAttachedSelector,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    priceQuoteErrors,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsErrors,
    priceDifference,
    verifyPaymentMethodResult,
    hasAddOnBeenAttached,
    isLodgingErrorCopyUpdateEnabled
  ): string => {
    // CT1261T-421: skip the price-difference type for a non-blocking decrease,
    // so a co-occurring error owns the modal + its analytics. Shares
    // isPriceDifferenceBlocking with getErrorTitles so the rendered modal and
    // its tracked type can't disagree.
    if (
      priceDifference.hasDifference &&
      priceDifference.amount &&
      isPriceDifferenceBlocking(
        priceDifference,
        isLodgingErrorCopyUpdateEnabled
      )
    ) {
      if (priceDifference.isIncrease && hasAddOnBeenAttached) {
        return "hotel_with_add_on_price_difference";
      }
      return "hotel_price_difference";
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      return "payment_verification_failed";
    }

    if (confirmationDetailsErrors.length > 0) {
      return "confirmation_details_failed";
    }

    if (scheduleBookError) {
      return "schedule_book_failed";
    }

    if (schedulePriceQuoteError) {
      return "schedule_price_quote_failed";
    }

    if (priceQuoteErrors.length > 0) {
      return "poll_price_quote_failed";
    }

    return "";
  }
);

export const getErrorTitles = createSelector(
  getHasError,
  getPriceQuoteErrors,
  getScheduleBookError,
  getSchedulePriceQuoteError,
  getConfirmationDetailsError,
  getPriceDifference,
  verifyPaymentMethodResultSelector,
  getTravelWalletItemsToApply,
  getModalType,
  hotelBookTypeSelector,
  hasAddOnBeenAttachedSelector,
  getRewardsAccounts,
  getHotelBookSelectedAvailabilityCheckInMinimumAge,
  getHasAttemptedReshop,
  getIsHotelUnavailableError,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    hasError,
    priceQuoteErrors,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsErrors,
    priceDifference,
    verifyPaymentMethodResult,
    travelWalletItemsToApply,
    type,
    hotelBookType,
    hasAddOnBeenAttached,
    accounts,
    checkInMinimumAge,
    hasAttemptedReshop,
    isUnavailableError,
    isLodgingErrorCopyUpdateEnabled
  ): ResolvedErrorTitles => {
    if (!hasError) return { title: "", subtitle: "", primaryButtonText: "" };

    const properties: ModalAlertProperties = {
      lob: "hotel",
      type,
      category: ModalCategoryType.TROUBLE,
      screen: ModalScreens.HOTELS_CHECKOUT,
      primary_button: "",
      secondary_button: "",
      modal_subtitle: "",
      modal_title: "",
      agent_title: "",
      agent_subtitle: "",
      step: "save_travelers",
      funnel: "hotels",
    };

    const callTrackEvent = (props: ModalAlertProperties) => {
      trackEvent({
        eventName: MODAL_ALERT,
        properties: props,
      });
    };

    // CT1261T-421: skip the price-difference modal for a non-blocking decrease
    // so a real booking error can show through instead of being masked by it.
    if (
      priceDifference.hasDifference &&
      priceDifference.amount &&
      isPriceDifferenceBlocking(
        priceDifference,
        isLodgingErrorCopyUpdateEnabled
      )
    ) {
      const primaryButtonText = textConstants.CONTINUE;

      const offerAmount =
        travelWalletItemsToApply.offerToApply?.amount.amount || 0;
      const creditsAmount =
        travelWalletItemsToApply.creditToApply?.amount.amount || 0;

      const walletItemCoverageChanged =
        (travelWalletItemsToApply.offerToApply ||
          travelWalletItemsToApply.creditToApply) &&
        Math.abs(offerAmount + creditsAmount) >=
          priceDifference.predictedTotal &&
        Math.abs(offerAmount + creditsAmount) < priceDifference.priceQuoteTotal;

      if (walletItemCoverageChanged) {
        properties.type = "hotel_price_difference";
        properties.primary_button = primaryButtonText;
        properties.secondary_button =
          hotelBookType === HotelBookType.DEFAULT
            ? textConstants.CHOOSE_ANOTHER_HOTEL
            : "";
        properties.modal_subtitle =
          textConstants.PRICE_INCREASE_WALLET_COVERAGE_SUBTITLE;
        properties.modal_title = travelWalletItemsToApply.creditToApply
          ? textConstants.PRICE_INCREASE_CREDITS_COVERAGE_TITLE
          : textConstants.PRICE_INCREASE_OFFER_COVERAGE_TITLE;
        properties.agent_subtitle = properties.modal_subtitle;
        properties.agent_title = properties.modal_title;
        properties.step = "save_travelers";
        callTrackEvent(properties);
        return {
          title: travelWalletItemsToApply.creditToApply
            ? textConstants.PRICE_INCREASE_CREDITS_COVERAGE_TITLE
            : textConstants.PRICE_INCREASE_OFFER_COVERAGE_TITLE,
          subtitle: textConstants.PRICE_INCREASE_WALLET_COVERAGE_SUBTITLE,
          primaryButtonText,
          secondaryButtonText:
            hotelBookType === HotelBookType.DEFAULT
              ? textConstants.CHOOSE_ANOTHER_HOTEL
              : undefined,
          icon: priceDifference.isIncrease
            ? textConstants.PRICE_INCREASE_ICON
            : textConstants.PRICE_DECREASE_ICON,
        };
      }

      if (priceDifference.isIncrease && hasAddOnBeenAttached) {
        properties.type = "hotel_with_add_on_price_difference";
        properties.primary_button = primaryButtonText;
        properties.secondary_button =
          hotelBookType === HotelBookType.DEFAULT
            ? textConstants.CHOOSE_ANOTHER_HOTEL
            : "";
        properties.modal_subtitle =
          textConstants.PRICE_INCREASE_WITH_ADD_ON_SUBTITLE_TRACKING;
        properties.modal_title = textConstants.getPriceDifferenceTitle(
          priceDifference.isIncrease,
          priceDifference.amount
        );
        properties.agent_subtitle = properties.modal_subtitle;
        properties.agent_title = properties.modal_title;
        properties.step = "save_travelers";
        callTrackEvent(properties);
        return {
          type: ErrorModalType.HOTEL_WITH_ADD_ONE_PRICE_DIFFERENCE,
          title: textConstants.getPriceDifferenceTitle(
            priceDifference.isIncrease,
            priceDifference.amount
          ),
          subtitle: textConstants.PRICE_INCREASE_WITH_ADD_ON_SUBTITLE,
          primaryButtonText,
          secondaryButtonText: textConstants.CHANGE_HOTEL,
          icon: textConstants.PRICE_INCREASE_ICON,
        };
      }

      // CT1261T-420: refreshed increase modal. Only the base increase changes;
      // the add-on and wallet-coverage sub-branches above keep their copy.
      const useNewIncreaseModal =
        isLodgingErrorCopyUpdateEnabled && priceDifference.isIncrease;
      // Round the increase UP (ceil); getTotalPriceText renders it in the quote
      // currency. The >= $1 threshold still uses the raw delta.
      const increaseAmountFiat: FiatPrice = {
        ...priceDifference.fiatAmount!,
        // Normalize FP subtraction noise to cents before rounding up, so an exact
        // whole-dollar delta (e.g. 150.3 − 100.3 = 50.000000000000014) isn't
        // ceil'd to $51.
        value: Math.ceil(
          Math.round(priceDifference.fiatAmount!.value * 100) / 100
        ),
      };

      const legacyTitle = textConstants.getPriceDifferenceTitle(
        priceDifference.isIncrease,
        priceDifference.amount
      );
      const legacySubtitle = priceDifference.isIncrease
        ? textConstants.PRICE_INCREASE_SUBTITLE
        : textConstants.PRICE_DECREASE_SUBTITLE;
      const legacySecondaryTracking =
        hotelBookType === HotelBookType.DEFAULT
          ? textConstants.CHOOSE_ANOTHER_HOTEL
          : "";

      const baseTitle = useNewIncreaseModal
        ? textConstants.PRICE_INCREASE_TITLE_V2(increaseAmountFiat)
        : legacyTitle;
      const baseSubtitle = useNewIncreaseModal
        ? textConstants.PRICE_INCREASE_SUBTITLE_V2
        : legacySubtitle;
      const basePrimaryButtonText = useNewIncreaseModal
        ? textConstants.ACCEPT_NEW_PRICE
        : primaryButtonText;

      properties.type = "hotel_price_difference";
      properties.primary_button = basePrimaryButtonText;
      properties.secondary_button = useNewIncreaseModal
        ? textConstants.VIEW_OTHER_OPTIONS
        : legacySecondaryTracking;
      properties.modal_subtitle = baseSubtitle;
      properties.modal_title = baseTitle;
      properties.agent_subtitle = properties.modal_subtitle;
      properties.agent_title = properties.modal_title;
      properties.step = "save_travelers";
      callTrackEvent(properties);
      return {
        type: ErrorModalType.HOTEL_PRICE_DIFFERENCE,
        title: baseTitle,
        subtitle: baseSubtitle,
        primaryButtonText: basePrimaryButtonText,
        secondaryButtonText: useNewIncreaseModal
          ? textConstants.VIEW_OTHER_OPTIONS
          : undefined,
        icon: priceDifference.isIncrease
          ? textConstants.PRICE_INCREASE_ICON
          : textConstants.PRICE_DECREASE_ICON,
      };
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      const returnProps = paymentTextConstants.PAYMENT_METHOD_ERROR_TITLES(
        verifyPaymentMethodResult,
        accounts[0],
        isCorpTenant(config.TENANT)
      );
      properties.primary_button = returnProps.primaryButtonText || "";
      properties.secondary_button = returnProps.secondaryButtonText || "";
      properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
      properties.modal_title = returnProps.title || "";
      properties.agent_subtitle =
        returnProps.agentSubtitle || properties.modal_subtitle;
      properties.agent_title = returnProps.agentTitle || properties.modal_title;
      properties.step = "select_payment";
      callTrackEvent(properties);
      return { ...returnProps };
    }

    if (isCorpTenant(config.TENANT)) {
      const errorString = confirmationDetailsErrors
        .map((error) => toErrorString(error))
        .join("-");
      if (errorString.toLowerCase().includes("frequent")) {
        const returnProps = textConstants.CORPORATE_LOYALTY_REQUIRED_TITLES;
        properties.primary_button = returnProps.primaryButtonText!;
        properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
        properties.modal_title = returnProps.title!;
        properties.agent_subtitle =
          returnProps.agentSubtitle || properties.modal_subtitle;
        properties.agent_title =
          returnProps.agentTitle || properties.modal_title;
        properties.step = "confirm_and_book";
        callTrackEvent(properties);

        return returnProps;
      }
    }

    // Before the per-error branches below so it wins over the normal sold-out
    // copy.
    if (hasAttemptedReshop && isUnavailableError) {
      const returnProps = textConstants.RESHOP_EXHAUSTED_TITLES;
      properties.primary_button = returnProps.primaryButtonText!;
      properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
      properties.modal_title = returnProps.title!;
      properties.agent_subtitle =
        returnProps.agentSubtitle || properties.modal_subtitle;
      properties.agent_title = returnProps.agentTitle || properties.modal_title;
      properties.step = "save_travelers";
      // Safe to overwrite `type` here, unlike the other branches: this state is
      // reachable only via reshop, and no CFAR spread keys off this value.
      properties.type = "reshop_failure";
      properties.is_reshop = true;
      callTrackEvent(properties);
      return returnProps;
    }

    if (confirmationDetailsErrors.length > 0) {
      const returnProps = getErrorMessage({
        error: confirmationDetailsErrors[0],
        checkInMinimumAge,
        isLodgingErrorCopyUpdateEnabled,
      });
      properties.primary_button = returnProps.primaryButtonText!;
      properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
      properties.modal_title = returnProps.title!;
      properties.agent_subtitle =
        returnProps.agentSubtitle || properties.modal_subtitle;
      properties.agent_title = returnProps.agentTitle || properties.modal_title;
      properties.step = "confirm_and_book";
      callTrackEvent(properties);
      return returnProps;
    }

    if (scheduleBookError) {
      // CT1261T-419: connection error on book — retryable modal when flag on.
      const returnProps = isLodgingErrorCopyUpdateEnabled
        ? textConstants.CONNECTION_ERROR_TITLES
        : textConstants.GENERIC_ERROR_TITLES();
      properties.primary_button = returnProps.primaryButtonText!;
      properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
      properties.modal_title = returnProps.title!;
      properties.agent_subtitle =
        returnProps.agentSubtitle || properties.modal_subtitle;
      properties.agent_title = returnProps.agentTitle || properties.modal_title;
      properties.step = "confirm_and_book";
      callTrackEvent(properties);
      return { ...returnProps, isConnection: isLodgingErrorCopyUpdateEnabled };
    }

    if (schedulePriceQuoteError) {
      // CT1261T-419: connection error on quote — retryable modal when flag on.
      const returnProps = isLodgingErrorCopyUpdateEnabled
        ? textConstants.CONNECTION_ERROR_TITLES
        : textConstants.GENERIC_ERROR_TITLES();
      properties.primary_button = returnProps.primaryButtonText!;
      properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
      properties.modal_title = returnProps.title!;
      properties.agent_subtitle =
        returnProps.agentSubtitle || properties.modal_subtitle;
      properties.agent_title = returnProps.agentTitle || properties.modal_title;
      properties.step = "save_travelers";
      callTrackEvent(properties);
      return { ...returnProps, isConnection: isLodgingErrorCopyUpdateEnabled };
    }

    if (priceQuoteErrors.length > 0) {
      const returnProps = getErrorMessage({
        error: priceQuoteErrors[0],
        checkInMinimumAge,
        isLodgingErrorCopyUpdateEnabled,
      });
      properties.primary_button = returnProps.primaryButtonText!;
      properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
      properties.modal_title = returnProps.title!;
      properties.agent_subtitle =
        returnProps.agentSubtitle || properties.modal_subtitle;
      properties.agent_title = returnProps.agentTitle || properties.modal_title;
      properties.step = "save_travelers";
      callTrackEvent(properties);
      return returnProps;
    }

    const returnProps = isLodgingErrorCopyUpdateEnabled
      ? textConstants.FALLBACK_ERROR_TITLES
      : textConstants.GENERIC_ERROR_TITLES();
    properties.primary_button = returnProps.primaryButtonText!;
    properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
    properties.modal_title = returnProps.title!;
    properties.agent_subtitle =
      returnProps.agentSubtitle || properties.modal_subtitle;
    properties.agent_title = returnProps.agentTitle || properties.modal_title;
    properties.step = "save_travelers";
    callTrackEvent(properties);
    return returnProps;
  }
);

// Reads the isConnection flag resolved by getErrorTitles (single source) to gate
// "Try again" → requote vs redoBook, not re-deriving the precedence (CT1261T-419).
export const getIsConnectionError = createSelector(
  getErrorTitles,
  (errorTitles): boolean => !!errorTitles.isConnection
);

export const getHotelBookViewedHotelDetailsProperties = createSelector(
  getViewedHotelDetailsProperties,
  (properties): ITrackingProperties<ViewedHotelDetailsProperties> => ({
    properties: {
      ...properties.properties,
    },
    encryptedProperties: [...properties.encryptedProperties],
  })
);

export const getReviewHotelDetailsCheckoutProperties = createSelector(
  getHotelBookViewedHotelDetailsProperties,
  getHotelBookSelectedAvailability,
  getHotelBookChosenProduct,
  getFromDateInBook,
  getUntilDateInBook,
  getNightCountInBook,
  getPurchasePriceQuotePricing,
  getSelectedRoomPolicyCompliance,
  getPaymentMethods,
  getHotelShopPersistentLodgingTrackingProperties,
  (
    viewedHotelDetailsProperties,
    hotelAvail,
    product,
    fromDate,
    toDate,
    nights,
    purchasePriceQuotePricing,
    roomInfoPolicyCompliance,
    paymentMethods,
    persistentLodgingTrackingProperties
  ): ITrackingProperties<ReviewDetailsHotelCheckoutProperties> => {
    const productTaxes = product
      ? Object.keys(product.taxBreakdown).reduce(
          (sum, key) => product.taxBreakdown[key].fiat.value + sum,
          0
        )
      : 0;
    const { hotelPricing } = purchasePriceQuotePricing ?? {};
    return {
      properties: {
        ...viewedHotelDetailsProperties.properties,
        ...hotelAvail?.trackingPropertiesV2?.properties,
        ...getPromotionTrackingProperties(hotelAvail),
        lodging_name: hotelAvail?.lodging.name || "",
        chain: "",
        brand: "",
        length_of_stay: nights || 0,
        sell_price_excluding_taxes_and_fees_usd:
          hotelPricing?.subtotal.fiat.value ||
          product?.tripTotal.fiat.value ||
          0,
        owed_now_excluding_taxes_and_fees:
          hotelPricing?.subtotal.fiat.value ||
          product?.tripTotal.fiat.value ||
          0,
        owed_now_taxes_and_fees:
          hotelPricing?.taxes.fiat.value || productTaxes || 0,
        owed_at_checkin_taxes_and_fees:
          hotelPricing?.taxes?.fiat.value || productTaxes || 0,
        pay_later_excluding_taxes_and_fees:
          hotelPricing?.taxes?.fiat.value || productTaxes || 0,
        pay_later_taxes_and_fees:
          hotelPricing?.taxes?.fiat.value || productTaxes || 0,
        pay_later_fees:
          hotelPricing?.feeBreakdown.total?.value ||
          product?.feeBreakdown.total?.value ||
          0,
        total_discount: 0,
        refundability:
          product?.cancellationPolicy.CancellationPolicy ===
          CancellationPolicyEnum.NonRefundable
            ? "non_refundable"
            : "free_cancellation",
        supply_revenue_usd:
          hotelPricing?.subtotal.fiat.value ||
          product?.tripTotal.fiat.value ||
          0,
        check_in_date: fromDate ? dayjs(fromDate).format("MM/DD/YYYY") : "",
        check_out_date: toDate ? dayjs(toDate).format("MM/DD/YYYY") : "",
        supplier: "",
        provider_type: "direct", // // TODO find out where this comes from -> "direct" | "retail" | "wholesale",
        adr: "",
        ...(isCorpTenant(config.TENANT) && {
          in_policy: roomInfoPolicyCompliance?.isInPolicy ?? true,
          hotel_loyalty_eligible: Boolean(
            (product as CorpRoomProduct)?.loyaltyProgramCode
          ),
          is_corporate: (product as CorpRoomProduct)?.isCorporate,
        }),
        is_preferred_cot: !!hotelAvail?.isPreferred,
        card_on_file: paymentMethods.length > 0,
        rate_provider: product?.providerName,
        provider_name: product?.providerName,
        ...(persistentLodgingTrackingProperties ?? {}),
      },
      encryptedProperties: [
        ...viewedHotelDetailsProperties.encryptedProperties,
        hotelAvail?.trackingPropertiesV2?.encryptedProperties ?? "",
      ],
    };
  }
);

export const getSelectedPaymentCardType = createSelector(
  getRewardsAccounts,
  getPaymentMethods,
  getSelectedPaymentMethodId,
  (accounts, paymentMethods, id): string => {
    const paymentMethod = paymentMethods.find((p) => p.id === id);

    if (!paymentMethod) {
      return "";
    }

    if (
      accounts[0]?.customerAccountRole === CustomerAccountRole.NonFinancialUser
    ) {
      return paymentMethod.cardType;
    }

    const matchingRewardsAccount = accounts.find(
      (a) =>
        a.lastFour === paymentMethod?.last4 ||
        a.lastFourVirtualCardNumbers?.includes(paymentMethod?.last4 || "")
    );

    if (matchingRewardsAccount && matchingRewardsAccount.productDisplayName) {
      return matchingRewardsAccount.productDisplayName;
    }

    // TODO: Remove this special logic to handle test credit cards once we have a proper test credit card
    // that shows the same last four digits on both rewards side and payment methods side.
    // The current logic is bad practice as we cannot properly test usage of credit card related info in staging.
    const isTestCard =
      window.__mclean_env__.ENV !== "production" &&
      paymentMethod.last4 &&
      TEST_CARD_LAST_FOURS.includes(paymentMethod.last4);

    if (isTestCard) {
      return accounts[0]?.productDisplayName ?? "";
    }

    return "";
  }
);

// The Pay Flex survey often renders before the customer picks a payment
// method, so fall back to the active/default rewards account to still report a
// card product.
export const getPayFlexCardProductUsed = createSelector(
  getSelectedPaymentCardType,
  getSelectedAccount,
  resolvePayFlexCardProduct
);

export const getHotelCfarDetailsAtPriceQuoteSelector = createSelector(
  getHotelCfarOfferChoicePropertiesSelector,
  hotelCfarQuoteFromChosenRoomProductSelector,
  additionalInfoFromChosenRoomProductSelector,
  (
    getHotelCfarOfferChoiceProperties,
    hotelCfarQuote,
    additionalInfo
  ): Cap1HotelCfarRRProperties => {
    const { cfar_choice, cfar_rr_hotel_choice } =
      getHotelCfarOfferChoiceProperties("book");
    let cfar_premium_usd: number | undefined;
    if (!!hotelCfarQuote && cfar_choice == 1) {
      cfar_premium_usd = hotelCfarQuote.premiumPrices.fiat.value;
    }
    const cfar_fee_is_refundable = additionalInfo?.shouldRefundCfarPremium;
    return {
      cfar_choice,
      cfar_rr_hotel_choice,
      cfar_premium_usd,
      cfar_fee_is_refundable,
    };
  }
);

export const getPriceQuoteHotelProperties = createSelector(
  getReviewHotelDetailsCheckoutProperties,
  getSession,
  getSelectedLodgingIndexInBook,
  getTravelWalletItemsToApply,
  getHotelCfarDetailsAtPriceQuoteSelector,
  getPriceDifference,
  (
    reviewHotelDetailsProperties,
    session,
    selectedLodgingIndex,
    travelWalletItemsToApply,
    hotelCfarDetails,
    priceDifference
  ): ITrackingProperties<PriceQuoteHotelProperties> => ({
    properties: {
      ...reviewHotelDetailsProperties.properties,
      success: false,
      booking_session_id: session?.value || "",
      lodging_row_index:
        selectedLodgingIndex != null ? selectedLodgingIndex : undefined,
      ...travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.properties,
      ...travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.properties,
      ...hotelCfarDetails,
      x_price_difference:
        parseInt(priceDifference?.amount ?? "0", 10) *
        (priceDifference?.isIncrease ? 1 : -1),
    },
    encryptedProperties: [
      ...reviewHotelDetailsProperties.encryptedProperties,
      travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
      travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
    ],
  })
);

export const getIsPaymentMethodVCN = createSelector(
  getPaymentMethodRewardsAccountId,
  getPaymentMethod,
  getRewardsAccounts,
  (paymentRewardsAccountId, paymentMethod, rewardsAccounts) => {
    if (!paymentRewardsAccountId || !paymentMethod || !rewardsAccounts)
      return false;

    const account = rewardsAccounts.find(
      (account) => account.accountReferenceId === paymentRewardsAccountId
    );

    return !!(
      account?.lastFourVirtualCardNumbers &&
      paymentMethod?.last4 &&
      account?.lastFourVirtualCardNumbers?.includes(paymentMethod?.last4)
    );
  }
);

export const getIsLodgingARecentlyViewed = createSelector(
  getHotelBookSelectedAvailability,
  (selectedLodging) => !!selectedLodging?.lastViewed
);

export const getIsAnySparkCardUsed = createSelector(
  getRewardsAccounts,
  getPaymentMethods,
  getSelectedPaymentMethodId,
  (accounts, paymentMethods, id): boolean | undefined => {
    // If user is NFU, they will only have one account, so we can check the first one
    if (
      accounts[0]?.customerAccountRole === CustomerAccountRole.NonFinancialUser
    ) {
      return undefined;
    }
    const paymentMethod = paymentMethods.find((p) => p.id === id);
    if (!paymentMethod?.last4) return undefined;
    const matchingAccount = accounts.find(
      (a) =>
        a.lastFour === paymentMethod.last4 ||
        a.lastFourVirtualCardNumbers?.includes(paymentMethod.last4 || "")
    );
    return matchingAccount !== undefined;
  }
);

export const getCompleteBuyHotelRewardsProperties = createSelector(
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentAccount,
  getSelectedPaymentCardType,
  (
    rewardPayment,
    rewardCurrencyPayment,
    account,
    cardType
  ): Pick<
    CompleteBuyHotelProperties,
    | "total_rewards_amount_usd"
    | "card_product_used"
    | "rewards_product_used"
    | "rewards_currency"
  > => ({
    total_rewards_amount_usd: rewardPayment?.value || 0,
    card_product_used: cardType,
    rewards_product_used: account?.productDisplayName || "",
    rewards_currency: rewardCurrencyPayment?.currency || "",
  })
);

export const getCompleteBuyHotelCfarOfferProperties = createSelector(
  getHotelBookChosenProduct,
  getHotelCfarQuoteInCheckout,
  additionalInfoFromChosenRoomProductSelector,
  getHotelCfarOfferChoicePropertiesSelector,
  getHasAddedCfarFromModalSelector,
  (
    roomProduct,
    hotelCfarQuote,
    additionalInfo,
    getHotelCfarOfferChoiceProperties,
    hasAddedCfarFromModal
  ): PartialCompleteBuyHotelHotelCfarOfferFacts =>
    getCompleteBuyHotelHotelCfarOfferFacts({
      roomProduct,
      hotelCfarQuote,
      additionalInfo,
      ...getHotelCfarOfferChoiceProperties("book"),
      hasAddedCfarFromModal,
    })
);

export const getCompleteBuyHotelProperties = createSelector(
  getReviewHotelDetailsCheckoutProperties,
  getCompleteBuyHotelRewardsProperties,
  getCompleteBuyHotelCfarOfferProperties,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getConfirmationDetails,
  getSession,
  getSelectedLodgingIndexInBook,
  getOffers,
  getTravelWalletCreditToApplyAmount,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletItemsToApply,
  getHotelShopEntryPoint,
  getIsPaymentMethodVCN,
  getIsLodgingARecentlyViewed,
  getSelectedRoomPolicyCompliance,
  getHotelShopRecommendedBasedOnPreferences,
  getHotelShopSelectedLodging,
  getHotelShopChosenProductHotelLoyaltyProgramCode,
  getUserSelectedTravelersList,
  getUserInfo,
  getPriceDifference,
  getHotelShopPersistentLodgingTrackingProperties,
  getIsAnySparkCardUsed,
  getReceivedCrossSellTrackingProperties,
  (
    reviewProperties,
    rewardsProperties,
    cfarOfferProperties,
    cardPayment,
    reservation,
    session,
    selectedLodgingIndex,
    offers,
    creditToApplyAmount,
    offerToApplyAmount,
    travelWalletItemsToApply,
    hotelShopEntryPoints,
    isPaymentMethodVCN,
    isRecentlyViewed,
    roomInfoPolicyCompliance,
    hotelShopRecommendedBasedOnPreferences,
    selectedLodging,
    loyaltyProgramCode,
    selectedCorpTravelers,
    userInfo,
    priceDifference,
    persistentLodgingTrackingProperties,
    isAnySparkCardUsed,
    crossSellTrackingProperties,
  ): ITrackingProperties<CompleteBuyHotelProperties> => {
    const isInPolicy = roomInfoPolicyCompliance?.isInPolicy ?? true;

    const { lodgingSelection }: { lodgingSelection?: string } =
      queryStringParser.parse(location.search);

    let parsedLodgingSelection: Place | undefined;

    const loyaltyAddedCount = selectedCorpTravelers.filter((guest) =>
      getLoyaltyNumberForChosenProduct(loyaltyProgramCode, guest)
    ).length;

    try {
      if (lodgingSelection) {
        parsedLodgingSelection = JSON.parse(
          decodeURIComponent(lodgingSelection)
        );
      }
    } catch (error) {
      console.error(error);
    }
    const isSelfBooked = selectedCorpTravelers.some(
      (passenger: Person) =>
        (passenger as ICorpPerson).isDefaultProfile ??
        (passenger.givenName === userInfo?.firstName &&
          passenger.surname === userInfo?.lastName)
    );

    return {
      properties: {
        ...reviewProperties.properties,
        ...rewardsProperties,
        ...cfarOfferProperties,
        success: false,
        total_card_amount_usd: cardPayment?.value || 0,
        agent_booking_fee_amount_usd: 0,
        booking_session_id: session?.value || "",
        reservation_id: reservation?.reservationId || "",
        lodging_row_index:
          selectedLodgingIndex != null ? selectedLodgingIndex : undefined,
        ...travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
          ?.properties,
        ...travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
          ?.properties,
        rooms_booked: reviewProperties.properties.rooms_searched,
        offer_amount_redeemed_usd: travelWalletItemsToApply.offerToApply
          ? Math.abs(offerToApplyAmount)
          : 0,
        offer_count: offers?.length,
        offer_used: !!travelWalletItemsToApply.offerToApply,
        credit_redeemed:
          !!travelWalletItemsToApply.creditToApply?.amount.amount,
        credit_amt_redeemed: travelWalletItemsToApply.creditToApply
          ? Math.abs(creditToApplyAmount)
          : 0,
        entry_type: hotelShopEntryPoints || undefined,
        is_vcn: isPaymentMethodVCN,
        amenity: getIsFreeBreakfast(selectedLodging?.price?.mealPlan?.kind)
          ? "free_breakfast"
          : undefined,
        is_recent: isRecentlyViewed,
        ...(isCorpTenant(config.TENANT) && {
          in_policy: isInPolicy,
          loyalty_added_count: loyaltyAddedCount,
          ...(!isInPolicy && {
            policy_reason: roomInfoPolicyCompliance?.reasons.join(", "),
          }),
        }),
        preferences_recommended: hotelShopRecommendedBasedOnPreferences,
        searched_market: parsedLodgingSelection?.searchTerm,
        google_place_id: parsedLodgingSelection?.placeId,
        total_guests_added: (reservation?.guests ?? []).length,
        city_name: selectedLodging?.lodging.city,
        state: selectedLodging?.lodging.state,
        nth_night_promo: getNthNightTrackingProperty(selectedLodging),
        meal_plan: selectedLodging?.price?.mealPlan?.kind,
        lodging_tripadvisor_rating:
          selectedLodging?.lodging.tripAdvisorReviews?.overallScore.toString(),
        booked_self_name_match: isSelfBooked,
        x_price_difference:
          parseInt(priceDifference?.amount ?? "0", 10) *
          (priceDifference?.isIncrease ? 1 : -1),
        loyalty_program_code: loyaltyProgramCode || undefined,
        user_has_loyalty_for_displayed_program: loyaltyProgramCode
          ? loyaltyAddedCount > 0
          : undefined,
        ...(persistentLodgingTrackingProperties ?? {}),
        ...(crossSellTrackingProperties ?? {}),
        ...(isCorpTenant(config.TENANT) && {
          card_matched_rewards_account: isAnySparkCardUsed,
        }),
      },
      encryptedProperties: [
        ...reviewProperties.encryptedProperties,
        travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
          ?.encryptedProperties ?? "",
        travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
          ?.encryptedProperties ?? "",
      ],
    };
  }
);

export const rewardsAccountMinimumRequirementStateSelector = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccountMinimumRequirementState =>
    getRewardsAccountMinimumRequirementState(rewardsAccounts)
);

export const getOffersRequest = createSelector(
  getHotelBookSelectedAvailability,
  getSearchedNightCountInBook,
  getFromDateInBook,
  getUntilDateInBook,
  getHotelShopChosenProduct,
  getPriceQuote,
  (
    selectedLodging,
    nightCount,
    fromDate,
    untilDate,
    chosenProduct,
    priceQuote
  ): OffersForProductsRequest | null => {
    if (
      selectedLodging &&
      nightCount &&
      fromDate &&
      untilDate &&
      chosenProduct
    ) {
      return {
        products: [
          {
            AvailableProduct: AvailableProductEnum.Lodging,
            id: selectedLodging?.lodging.id,
            merchant: MerchantOfRecord.Hopper,
            lengthOfStay: nightCount,
            stayPeriod: {
              from: dayjs(fromDate).format("YYYY-MM-DD"),
              until: dayjs(untilDate).format("YYYY-MM-DD"),
            },
            isPreferred: selectedLodging.isPreferred,
            isFreeCancel: selectedLodging.isFreeCancel,
            collectionType: selectedLodging.lodgingCollection,
            belongsToPackage: false,
            productPricing: {
              productClass: ProductClassEnum.Economy,
              currency:
                selectedLodging.price?.totalPrice.fiat.currencyCode ?? "USD",
              basePrice:
                chosenProduct.totalDiscountAware.priceWithNoDiscounts.fiat
                  .value,
              grossBookValue:
                chosenProduct.totalDiscountAware.priceWithUnmanagedDiscounts
                  .fiat.value,
              ancillaryTotal: 0, // hardcoded bc Tysons does this https://github.com/hopper-org/Tysons/blob/1167df0bdaa8ae2a5255ea0203438deacff071ae/server/src/main/scala/com/hopper/tysons/service/hotels/availability/converter/ImpFastAvailabilityResponseConverter.scala#L552
              preDiscountTotal:
                priceQuote?.pricing.payNowTotal.fiat.value ??
                chosenProduct.total.fiat.value,
              preDiscountGrandTotal:
                priceQuote?.pricing.tripTotal.fiat.value ??
                chosenProduct.tripTotal.fiat.value,
              sellPrice:
                priceQuote?.pricing.tripTotal.fiat.value ??
                chosenProduct.tripTotal.fiat.value,
            },
          },
        ],
      };
    }
    return null;
  }
);

export const getRewardAccountsWithPaymentAccounts = createSelector(
  getRewardsAccounts,
  getPaymentMethods,
  (rewardsAccounts, paymentMethods) => {
    const accountsWithSavedPayment = paymentMethods.reduce(
      (savedPayments, currentAcc) => {
        const matchingRewardsAcct = rewardsAccounts.find(
          (rewards) =>
            rewards.lastFour === currentAcc.last4 ||
            rewards.lastFourVirtualCardNumbers?.includes(currentAcc.last4 || "")
        );

        if (matchingRewardsAcct) {
          savedPayments.push(matchingRewardsAcct);
        }
        const isTestCard =
          window.__mclean_env__.ENV !== "production" &&
          currentAcc.last4 &&
          TEST_CARD_LAST_FOURS.includes(currentAcc.last4);
        if (isTestCard) {
          savedPayments.push(rewardsAccounts[0]);
        }

        return savedPayments;
      },
      [] as RewardsAccount[]
    );
    return accountsWithSavedPayment;
  }
);

export const getEarnValuesByRewardAcctId = (state: IStoreState) =>
  state.hotelBook.earnValuesByRewardAcctId;

export const getPriceQuoteRequest = (state: IStoreState) =>
  state.hotelBook.priceQuoteRequest;

// Corporate Travel
export const getTripPurpose = (state: IStoreState) =>
  state.hotelBook.tripPurpose;

export const getTripDescription = (state: IStoreState) =>
  state.hotelBook.tripDescription;
